<script setup>
import apiClient from "@/apiClient.js";
import { apiUrl } from "@/vars";
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import { useAuthStore } from "@/stores/AuthStore";
import { useTicketStore } from "@/stores/TicketStore";
import { useUndoToastStore } from "@/stores/UndoToastStore";
import { useVacancyStore } from "@/stores/VacancyStore";
import dayjs from "dayjs";
import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  ArrowDownOnSquareIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeOpenIcon,
  EyeIcon,
  FlagIcon,
  FolderArrowDownIcon,
  InformationCircleIcon,
  PaperClipIcon,
  PhoneIcon,
  PlusCircleIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/vue/24/solid";

const route = useRoute();
const router = useRouter();
const breadcrumbStore = useBreadcrumbStore();
const auth = useAuthStore();
const ticketStore = useTicketStore();
const undoToast = useUndoToastStore();
const vacancyStore = useVacancyStore();

const ticketId = ref(null);
const ticket = ref(null);
const responseText = ref(null);
const responseInput = ref(null);
const attachments = ref([]);
const sendingReply = ref(false);
const steerOpen = ref(false);
const steerPrompt = ref("");
const steerPromptInput = ref(null);
const steerFileInput = ref(null);
const steerAttachments = ref([]);
const steeringReply = ref(false);
const steerError = ref("");
const sendAnonymously = ref(0);
const closeAfterReply = ref(false);
const quoteActionLoading = ref({});
const acceptanceModal = ref(null);
const quoteDeclineOpen = ref({});
const quoteDeclineReasons = ref({});
const callLogOpen = ref(false);
const loggingCall = ref(false);
const callLogContact = ref(null);
const callLogContactQuery = ref("");
const callLogForm = ref({
  direction: "outbound",
  phone: "",
  note: "",
});
const creatingVacancy = ref(false);
const vacancyProgress = ref(0);
const vacancyProgressLabel = ref("");
const infoOpen = ref(false);
const infoTab = ref("activity");
const historyLoading = ref(false);
const historyEntries = ref([]);
const retryingActionId = ref(null);
const actionMenuOpen = ref(false);
const actionMenuRef = ref(null);
const activeTicketAction = ref(null);
const exportingEvidence = ref(false);
const updatingSpam = ref(false);
const rawEmailItemId = ref(null);
const replyCcInput = ref("");
const replyCcInputRef = ref(null);
const replyCcRecipients = ref([]);
const replyCcError = ref("");
const replyCcVisible = ref(false);
const replyCcContactSuggestions = ref([]);
const replyCcSuggestionsVisible = ref(false);
const replyCcSearchLoading = ref(false);
const replyCcSelectedSuggestionIndex = ref(-1);
const replyRecipient = ref(null);
const replyRecipientControlsOpen = ref(false);
const replyRecipientEditing = ref(false);
const replyRecipientQuery = ref("");
const filingPurchaseInvoiceAttachmentId = ref(null);
const transferringTicketInvoices = ref(false);
const purchaseInvoiceOpen = ref(false);
const activePurchaseInvoice = ref(null);
const purchaseInvoiceSupplier = ref(null);
const purchaseInvoiceSupplierInputKey = ref(0);
const savingPurchaseInvoice = ref(false);
const extractionFallbackVisible = ref(false);
const payrollTransferOpen = ref(false);
const payrollTransferLoading = ref(false);
const payrollTransferSubmitting = ref(false);
const payrollTransferError = ref("");
const payrollTransferPreview = ref(null);
const selectedPayrollAttachmentIds = ref(new Set());
const payrollDestinationMode = ref("new");
const selectedPayrollRunId = ref(null);
let vacancyProgressTimer = null;
let purchaseInvoicePollTimer = null;
let purchaseInvoiceFallbackTimer = null;
let replyCcSearchTimer = null;
let replyCcSearchRequest = 0;

const purchaseInvoiceForm = reactive({
  supplier_id: "",
  invoice_date: "",
  due_date: "",
  invoice_number: "",
  description: "",
  gross_amount: "",
  net_amount: "",
  vat_amount: "",
  vat_treatment: "needs_review",
  category: "",
  analysis: "",
  notes: "",
});

const vatTreatmentOptions = [
  { value: "standard_20", label: "20% VAT" },
  { value: "no_vat", label: "No VAT" },
  { value: "zero_rated", label: "Zero rated" },
  { value: "exempt", label: "Exempt" },
  { value: "reverse_charge", label: "Reverse charge" },
  { value: "needs_review", label: "Needs review" },
];

const actionLabels = {
  motia_fuel_report: "Motia fuel import",
  moneypenny_contact: "Moneypenny caller match",
  website_contact: "Website contact match",
  vacancy_application: "Job application",
  job_vacancy_application: "Job application",
  purchase_invoice_intake: "Supplier invoice intake",
};

const internalHistoryEvents = new Set([
  "internal_note_added",
  "phone_call_logged",
  "ticket_marked_spam",
  "ticket_restored_from_spam",
  "ticket_closed",
  "ticket_reopened",
  "ticket_flagged",
  "ticket_unflagged",
  "ticket_deleted",
  "ticket_restored",
  "spam_retention_extended",
  "spam_purge_blocked",
  "payroll_timesheets_transferred",
]);

var relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const backToListRoute = computed(() => ({
  path: "/tickets",
  query: route.query,
}));

const canFilePurchaseInvoices = computed(() =>
  auth.hasAnyRole(["finance-department", "super"]),
);
const canSeePurchaseInvoiceAction = computed(() => auth.hasFinanceRole());
const unfiledPurchaseInvoiceAttachments = computed(() =>
  (ticket.value?.items ?? [])
    .flatMap((item) => item?.attachments ?? [])
    .filter((attachment) => !attachment?.purchase_invoice),
);

const canExportEvidence = computed(() => auth.hasRole("super"));
const canTransferPayrollTimesheets = computed(() =>
  auth.hasAnyRole(["finance-department", "super"]),
);

const payrollAttachmentCanBeSelected = (attachment) => {
  if (!attachment?.supported) {
    return false;
  }
  if (!attachment.transferred) {
    return true;
  }
  if (!attachment.movable) {
    return false;
  }
  if (payrollDestinationMode.value === "new") {
    return Boolean(payrollTransferPreview.value?.can_create_run);
  }

  return (
    Number(attachment.payroll_run_id) !== Number(selectedPayrollRunId.value)
  );
};

const selectablePayrollAttachments = computed(() =>
  (payrollTransferPreview.value?.attachments || []).filter(
    payrollAttachmentCanBeSelected,
  ),
);

const selectedPayrollAttachmentCount = computed(
  () => selectedPayrollAttachmentIds.value.size,
);

const payrollDestinationReady = computed(() => {
  if (payrollDestinationMode.value === "new") {
    return Boolean(payrollTransferPreview.value?.can_create_run);
  }

  return Boolean(selectedPayrollRunId.value);
});

const payrollTransferSubmitLabel = computed(() => {
  if (payrollTransferSubmitting.value) {
    return "Sending…";
  }

  const count = selectedPayrollAttachmentCount.value || "";
  return payrollDestinationMode.value === "new"
    ? `Create run & send ${count}`
    : `Add ${count} to pay run`;
});

const extractionPending = computed(() =>
  ["queued", "processing"].includes(
    String(activePurchaseInvoice.value?.extraction_status || "").toLowerCase(),
  ),
);

const extractionStatusText = computed(() => {
  const status = String(
    activePurchaseInvoice.value?.extraction_status || "",
  ).toLowerCase();

  if (status === "completed") {
    return "Invoice details extracted. Review and save any changes.";
  }
  if (status === "failed") {
    return (
      activePurchaseInvoice.value?.extraction_error ||
      "Extraction failed. Enter the details manually."
    );
  }
  if (status === "blocked") {
    return (
      activePurchaseInvoice.value?.extraction_error ||
      "Automatic extraction was held for finance review."
    );
  }
  if (status === "processing") {
    return "Reading invoice attachment...";
  }
  if (status === "queued") {
    return "Queued for invoice extraction...";
  }

  return "Enter the invoice details for future VAT matching.";
});

const loadTicket = async (id) => {
  if (!id) {
    return;
  }

  ticketId.value = id;
  try {
    const { data } = await apiClient.get("/api/ticket/" + id);
    ticket.value = data;
    responseText.value = ticket.value.draft;
    infoOpen.value = false;
    infoTab.value = "activity";
    rawEmailItemId.value = null;
    replyCcInput.value = "";
    replyCcRecipients.value = [];
    replyCcError.value = "";
    replyCcVisible.value = false;
    replyCcContactSuggestions.value = [];
    replyCcSuggestionsVisible.value = false;
    replyCcSearchLoading.value = false;
    replyCcSelectedSuggestionIndex.value = -1;
    clearTimeout(replyCcSearchTimer);
    replyCcSearchRequest += 1;
    replyRecipientControlsOpen.value = false;
    replyRecipientEditing.value = false;
    replyRecipientQuery.value = "";
    const latestAddressedItem = (Array.isArray(ticket.value.items)
      ? ticket.value.items.slice().reverse()
      : []
    ).find((item) => item?.contact || item?.recipient);
    replyRecipient.value = latestAddressedItem?.contact || latestAddressedItem?.recipient || null;
    breadcrumbStore.setValue(ticket.value.subject);
    await loadTicketHistory();
  } catch (error) {
    console.error(error);
  }
};

onMounted(() => {
  loadTicket(route.params.id);
  document.addEventListener("click", handleDocumentClick);
});

watch(
  () => route.fullPath,
  () => {
    closeActionMenu();
  },
);

onBeforeRouteUpdate((to, from, next) => {
  if (to.params.id && to.params.id !== ticketId.value) {
    loadTicket(to.params.id);
  }
  next();
});

const onFileChange = (event) => {
  attachments.value = [...event.target.files];
};

const inlineViewableAttachmentExtensions = new Set([
  "bmp",
  "gif",
  "jpeg",
  "jpg",
  "pdf",
  "png",
  "svg",
  "webp",
]);

const attachmentSupportsInlineViewing = (attachment) => {
  const filename = String(attachment?.file?.filename || "");
  const extension = filename.split(".").pop()?.toLowerCase() || "";

  return inlineViewableAttachmentExtensions.has(extension);
};

const attachmentUrl = (attachment, options = {}) => {
  const params = new URLSearchParams();
  if (options.download) {
    params.set("download", "1");
  }

  const query = params.toString();

  return (
    `${apiUrl}/ticket/${ticket.value.id}/attachment/${attachment.id}` +
    (query ? `?${query}` : "")
  );
};

const attachmentApiPath = (attachment) =>
  `/api/ticket/${ticket.value.id}/attachment/${attachment.id}`;

const attachmentInlineMimeType = (attachment) => {
  const filename = String(attachment?.file?.filename || "").toLowerCase();

  if (filename.endsWith(".pdf")) {
    return "application/pdf";
  }

  if (filename.endsWith(".png")) {
    return "image/png";
  }

  if (filename.endsWith(".gif")) {
    return "image/gif";
  }

  if (filename.endsWith(".webp")) {
    return "image/webp";
  }

  if (filename.endsWith(".svg")) {
    return "image/svg+xml";
  }

  return "image/jpeg";
};

const openAttachment = async (attachment) => {
  const previewWindow = window.open("", "_blank");

  try {
    if (!previewWindow) {
      throw new Error("Preview window was blocked by the browser.");
    }

    previewWindow.document.title = "Opening attachment...";
    previewWindow.document.body.innerHTML =
      '<div style="font-family: sans-serif; padding: 24px; color: #0f172a;">Opening attachment...</div>';

    const response = await apiClient.get(attachmentApiPath(attachment), {
      responseType: "blob",
    });
    const sourceBlob =
      response.data instanceof Blob ? response.data : new Blob([response.data]);
    const objectUrl = window.URL.createObjectURL(
      sourceBlob.type && sourceBlob.type !== "application/octet-stream"
        ? sourceBlob
        : new Blob([sourceBlob], {
            type: attachmentInlineMimeType(attachment),
          }),
    );

    previewWindow.location.replace(objectUrl);

    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 300_000);
  } catch (error) {
    if (previewWindow) {
      previewWindow.close();
    }

    alert(apiErrorMessage(error, "Unable to open attachment."));
  }
};

const supplierOutput = (supplier) => supplier?.company ?? "";

const moneyToInput = (value) => {
  if (value === null || value === undefined || Number(value) <= 0) {
    return "";
  }

  return (Number(value) / 100).toFixed(2);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "£0.00";
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(Number(value) / 100);
};

const apiErrorMessage = (error, fallback) => {
  const message = String(error?.response?.data?.message ?? "").trim();
  if (message) {
    return message;
  }

  const errorMessage = String(error?.response?.data?.error ?? "").trim();
  if (errorMessage) {
    return errorMessage;
  }

  const errors = error?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors)
      .flat()
      .find((value) => String(value ?? "").trim() !== "");

    if (first) {
      return String(first);
    }
  }

  return fallback;
};

const applyPurchaseInvoice = (invoice) => {
  activePurchaseInvoice.value = invoice ?? null;
  purchaseInvoiceSupplier.value = invoice?.supplier ?? null;
  Object.assign(purchaseInvoiceForm, {
    supplier_id: invoice?.supplier_id ?? "",
    invoice_date: invoice?.invoice_date ?? "",
    due_date: invoice?.due_date ?? "",
    invoice_number: invoice?.invoice_number ?? "",
    description: invoice?.description ?? "",
    gross_amount: moneyToInput(invoice?.gross_amount),
    net_amount: moneyToInput(invoice?.net_amount),
    vat_amount: moneyToInput(invoice?.vat_amount),
    vat_treatment: invoice?.vat_treatment ?? "needs_review",
    category: invoice?.category ?? "",
    analysis: invoice?.analysis ?? "",
    notes: invoice?.notes ?? "",
  });
};

const stopPurchaseInvoicePolling = () => {
  if (purchaseInvoicePollTimer) {
    window.clearTimeout(purchaseInvoicePollTimer);
    purchaseInvoicePollTimer = null;
  }
  if (purchaseInvoiceFallbackTimer) {
    window.clearTimeout(purchaseInvoiceFallbackTimer);
    purchaseInvoiceFallbackTimer = null;
  }
};

const queuePurchaseInvoicePoll = () => {
  if (!activePurchaseInvoice.value?.id) {
    return;
  }

  if (purchaseInvoicePollTimer) {
    window.clearTimeout(purchaseInvoicePollTimer);
  }

  purchaseInvoicePollTimer = window.setTimeout(() => {
    pollPurchaseInvoiceExtraction();
  }, extractionFallbackVisible.value ? 5000 : 2000);
};

const startPurchaseInvoiceFallbackTimer = () => {
  if (purchaseInvoiceFallbackTimer) {
    window.clearTimeout(purchaseInvoiceFallbackTimer);
  }

  extractionFallbackVisible.value = false;
  purchaseInvoiceFallbackTimer = window.setTimeout(() => {
    extractionFallbackVisible.value = true;
  }, 30000);
};

const pollPurchaseInvoiceExtraction = async () => {
  if (!activePurchaseInvoice.value?.id) {
    return;
  }

  try {
    const { data } = await apiClient.get(
      `/api/purchase-invoices/${activePurchaseInvoice.value.id}`,
      {
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    applyPurchaseInvoice(data);

    if (extractionPending.value) {
      queuePurchaseInvoicePoll();
    } else {
      stopPurchaseInvoicePolling();
    }
  } catch (error) {
    console.error(error);
    extractionFallbackVisible.value = true;
  }
};

const openPurchaseInvoiceModal = (invoice) => {
  applyPurchaseInvoice(invoice);
  purchaseInvoiceOpen.value = true;
  purchaseInvoiceSupplierInputKey.value++;

  if (extractionPending.value) {
    startPurchaseInvoiceFallbackTimer();
    queuePurchaseInvoicePoll();
  }
};

const closePurchaseInvoiceModal = () => {
  purchaseInvoiceOpen.value = false;
  activePurchaseInvoice.value = null;
  purchaseInvoiceSupplier.value = null;
  extractionFallbackVisible.value = false;
  stopPurchaseInvoicePolling();
};

const selectPurchaseInvoiceSupplier = (supplier) => {
  purchaseInvoiceSupplier.value = supplier ?? null;
  purchaseInvoiceForm.supplier_id = supplier?.id ?? "";
  if (supplier?.default_vat_treatment) {
    purchaseInvoiceForm.vat_treatment = supplier.default_vat_treatment;
  } else if (supplier?.vat_number) {
    purchaseInvoiceForm.vat_treatment = "standard_20";
  }
  if (supplier?.default_vat_category) {
    purchaseInvoiceForm.category = supplier.default_vat_category;
  }
  if (supplier?.default_vat_analysis) {
    purchaseInvoiceForm.analysis = supplier.default_vat_analysis;
  }
};

const clearPurchaseInvoiceSupplier = () => {
  purchaseInvoiceSupplier.value = null;
  purchaseInvoiceForm.supplier_id = "";
  purchaseInvoiceSupplierInputKey.value++;
};

const fileAttachmentAsPurchaseInvoice = async (attachment) => {
  if (!ticket.value?.id || !attachment?.id) {
    return;
  }

  if (attachment.purchase_invoice?.id) {
    try {
      const { data } = await apiClient.get(
        `/api/purchase-invoices/${attachment.purchase_invoice.id}`,
      );
      openPurchaseInvoiceModal(data);
    } catch (error) {
      alert(apiErrorMessage(error, "Unable to load purchase invoice."));
    }
    return;
  }

  filingPurchaseInvoiceAttachmentId.value = attachment.id;
  try {
    const { data } = await apiClient.post(
      `/api/ticket/${ticket.value.id}/attachment/${attachment.id}/purchase-invoice`,
      { run_extraction: true },
      {
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    openPurchaseInvoiceModal(data?.purchase_invoice);
    await loadTicket(ticket.value.id);
  } catch (error) {
    alert(apiErrorMessage(error, "Unable to file purchase invoice."));
  } finally {
    filingPurchaseInvoiceAttachmentId.value = null;
  }
};

const transferTicketToSupplierInvoices = async () => {
  if (!ticket.value?.id || !unfiledPurchaseInvoiceAttachments.value.length) {
    return;
  }

  transferringTicketInvoices.value = true;
  try {
    const { data } = await apiClient.post(
      `/api/ticket/${ticket.value.id}/purchase-invoices`,
      {},
      { suppressAutoReload: true, suppressErrorAlert: true },
    );
    await loadTicket(ticket.value.id);
    alert(
      `${data?.created_count ?? 0} attachment(s) sent to Supplier Invoices. Extraction will continue in the background.`,
    );
  } catch (error) {
    alert(apiErrorMessage(error, "Unable to transfer this ticket to Supplier Invoices."));
  } finally {
    transferringTicketInvoices.value = false;
  }
};

const openPayrollTransferModal = async () => {
  if (!ticket.value?.id || payrollTransferLoading.value) {
    return;
  }

  payrollTransferOpen.value = true;
  payrollTransferLoading.value = true;
  payrollTransferError.value = "";
  payrollTransferPreview.value = null;
  selectedPayrollAttachmentIds.value = new Set();

  try {
    const { data } = await apiClient.get(
      `/api/ticket/${ticket.value.id}/payroll-timesheets`,
      { suppressAutoReload: true, suppressErrorAlert: true },
    );
    payrollTransferPreview.value = data;
    const currentDraftRun =
      data?.payroll_run?.status === "draft" ? data.payroll_run : null;
    if (currentDraftRun) {
      selectPayrollDestination("existing", currentDraftRun.id);
    } else if (data?.can_create_run) {
      selectPayrollDestination("new");
    } else if (data?.available_payroll_runs?.length) {
      selectPayrollDestination("existing", data.available_payroll_runs[0].id);
    }
  } catch (error) {
    payrollTransferError.value = apiErrorMessage(
      error,
      "Unable to prepare the payroll transfer.",
    );
  } finally {
    payrollTransferLoading.value = false;
  }
};

const closePayrollTransferModal = () => {
  if (payrollTransferSubmitting.value) {
    return;
  }

  payrollTransferOpen.value = false;
  payrollTransferPreview.value = null;
  payrollTransferError.value = "";
  selectedPayrollAttachmentIds.value = new Set();
  payrollDestinationMode.value = "new";
  selectedPayrollRunId.value = null;
};

const selectPayrollDestination = (mode, runId = null) => {
  payrollDestinationMode.value = mode;
  selectedPayrollRunId.value =
    mode === "existing"
      ? Number(
          runId ||
            payrollTransferPreview.value?.available_payroll_runs?.[0]?.id,
        ) || null
      : null;
  payrollTransferError.value = "";
  selectedPayrollAttachmentIds.value = new Set(
    (payrollTransferPreview.value?.attachments || [])
      .filter(payrollAttachmentCanBeSelected)
      .map((attachment) => attachment.id),
  );
};

const togglePayrollAttachment = (attachment) => {
  if (!payrollAttachmentCanBeSelected(attachment)) {
    return;
  }

  const next = new Set(selectedPayrollAttachmentIds.value);
  if (next.has(attachment.id)) {
    next.delete(attachment.id);
  } else {
    next.add(attachment.id);
  }
  selectedPayrollAttachmentIds.value = next;
};

const transferTicketToPayroll = async () => {
  if (
    !ticket.value?.id ||
    payrollTransferSubmitting.value ||
    !payrollDestinationReady.value ||
    selectedPayrollAttachmentCount.value === 0
  ) {
    return;
  }

  payrollTransferSubmitting.value = true;
  payrollTransferError.value = "";
  try {
    const { data } = await apiClient.post(
      `/api/ticket/${ticket.value.id}/payroll-timesheets`,
      {
        attachment_ids: [...selectedPayrollAttachmentIds.value],
        destination_mode: payrollDestinationMode.value,
        payroll_run_id:
          payrollDestinationMode.value === "existing"
            ? selectedPayrollRunId.value
            : null,
      },
      { suppressAutoReload: true, suppressErrorAlert: true },
    );
    payrollTransferOpen.value = false;
    payrollTransferPreview.value = null;
    selectedPayrollAttachmentIds.value = new Set();
    await loadTicket(ticket.value.id);
    const movedMessage = data?.moved_count
      ? ` ${data.moved_count} moved from another open pay run.`
      : "";
    alert(
      `${data?.created_count ?? 0} timesheet attachment(s) sent to ${data?.payroll_run?.name || "payroll"}.${movedMessage} Best AI extraction and verification has been queued.`,
    );
  } catch (error) {
    payrollTransferError.value = apiErrorMessage(
      error,
      "Unable to transfer the timesheets to payroll.",
    );
  } finally {
    payrollTransferSubmitting.value = false;
  }
};

const savePurchaseInvoiceDetails = async () => {
  if (!activePurchaseInvoice.value?.id) {
    return;
  }

  if (!purchaseInvoiceForm.gross_amount) {
    alert(
      "Gross amount is required before the invoice can be used for VAT matching.",
    );
    return;
  }

  savingPurchaseInvoice.value = true;
  try {
    const payload = {};
    Object.entries(purchaseInvoiceForm).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        payload[key] = value;
      }
    });
    payload.supplier_id = purchaseInvoiceSupplier.value?.id ?? null;

    const { data } = await apiClient.put(
      `/api/purchase-invoices/${activePurchaseInvoice.value.id}`,
      payload,
    );
    applyPurchaseInvoice(data);
    await loadTicket(ticket.value.id);
    closePurchaseInvoiceModal();
  } catch (error) {
    alert(apiErrorMessage(error, "Unable to save purchase invoice."));
  } finally {
    savingPurchaseInvoice.value = false;
  }
};

const actionBanners = computed(() =>
  (Array.isArray(ticket.value?.actions) ? ticket.value.actions : []).map(
    (action) => {
      const status = String(action?.status || "").toLowerCase();
      const label = actionLabels[action?.type] || "Ticket action";
      const message =
        action?.summary || action?.error || "Processing ticket action.";

      if (status === "completed") {
        return {
          id: action.id,
          label,
          message,
          classes:
            "rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200",
          badge: "Completed",
          badgeClasses: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-100",
        };
      }

      if (status === "failed") {
        return {
          id: action.id,
          label,
          message,
          retryable: action?.type === "website_contact",
          classes:
            "rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200",
          badge: "Failed",
          badgeClasses: "bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-100",
        };
      }

      return {
        id: action.id,
        label,
        message,
        classes:
          "rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
        badge: "Pending",
        badgeClasses: "bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-100",
      };
    },
  ),
);

const retryTicketAction = async (action) => {
  if (!ticket.value?.id || !action?.id || retryingActionId.value) {
    return;
  }

  retryingActionId.value = action.id;

  try {
    await apiClient.post(
      `/api/ticket/${ticket.value.id}/action/${action.id}/retry`,
    );
    await loadTicket(ticket.value.id);
  } catch (error) {
    alert(apiErrorMessage(error, "Unable to retry the ticket action."));
  } finally {
    retryingActionId.value = null;
  }
};

const conversationItems = computed(() =>
  Array.isArray(ticket.value?.items) ? ticket.value.items.slice().reverse() : [],
);

const quoteSendMetadata = (item) =>
  (Array.isArray(item?.metadata) ? item.metadata : []).find(
    (entry) => entry?.key === "quote_send" && (entry?.quote || entry?.value?.quote_id),
  ) || null;

const quoteFromMetadata = (metadata) => {
  if (!metadata) return null;

  return {
    id: metadata.quote?.id ?? metadata.value?.quote_id ?? metadata.subject_id,
    status: metadata.quote?.status ?? metadata.value?.status_at_send,
    display_name:
      metadata.quote?.display_name ||
      metadata.quote?.description ||
      metadata.value?.reference_text ||
      "Quote",
    reference_text:
      metadata.quote?.reference_text || metadata.value?.reference_text || null,
    sent_at: metadata.quote?.sent_at || metadata.value?.sent_at || null,
  };
};

const attachedQuotes = computed(() =>
  (Array.isArray(ticket.value?.quotes) ? ticket.value.quotes : []).map((quote) => ({
    id: quote.id,
    status: quote.status,
    display_name:
      quote.display_name ||
      quote.description ||
      quote.reference?.name ||
      quote.reference?.txt ||
      "Quote",
    reference_text:
      quote.reference?.txt ||
      quote.reference_text ||
      (quote.reference?.type?.prefix && quote.reference?.identifier
        ? `${quote.reference.type.prefix}${quote.reference.identifier}`
        : null),
    decline_reason: quote.decline_reason,
    decline_reason_label: quote.decline_reason_label,
    sent_at: quote.sent_at,
    sent_via_ticket: Boolean(quote.sent_via_ticket),
    recipient_contact: quote.recipient_contact || null,
    updated_at: quote.updated_at,
    created_at: quote.created_at,
  })),
);

const draftAttachedQuotes = computed(() =>
  attachedQuotes.value.filter(
    (quote) => String(quote?.status || "").toLowerCase() === "draft",
  ),
);

const quoteStatusLabel = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "accepted") return "Accepted";
  if (value === "declined") return "Declined";
  if (value === "sent") return "Sent";
  if (value === "draft") return "Draft";
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "Quote";
};

const quoteStatusClasses = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "accepted") {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100";
  }
  if (value === "declined") {
    return "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-100";
  }
  if (value === "sent") {
    return "bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-100";
  }
  return "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
};

const quoteDeclineReasonOptions = [
  { value: "no_response", label: "No response" },
  { value: "too_expensive", label: "Too expensive" },
  { value: "unspecified", label: "Unspecified" },
  { value: "event_cancelled", label: "Event cancelled" },
  { value: "another_supplier_chosen", label: "Another supplier chosen" },
];

const canUpdateQuoteStatus = (quote) =>
  !["accepted", "declined"].includes(String(quote?.status || "").toLowerCase());

const isQuoteActionLoading = (quote) => Boolean(quoteActionLoading.value[quote.id]);

const isQuoteDeclineOpen = (quote) => Boolean(quoteDeclineOpen.value[quote.id]);

const selectedQuoteDeclineReason = (quote) =>
  quoteDeclineReasons.value[quote.id] || "";

const showQuoteDeclineReason = (quote) => {
  if (!quote?.id) return;

  quoteDeclineOpen.value = {
    ...quoteDeclineOpen.value,
    [quote.id]: true,
  };
  quoteDeclineReasons.value = {
    ...quoteDeclineReasons.value,
    [quote.id]: selectedQuoteDeclineReason(quote),
  };
};

const cancelQuoteDecline = (quote) => {
  if (!quote?.id) return;

  const nextOpen = { ...quoteDeclineOpen.value };
  const nextReasons = { ...quoteDeclineReasons.value };
  delete nextOpen[quote.id];
  delete nextReasons[quote.id];
  quoteDeclineOpen.value = nextOpen;
  quoteDeclineReasons.value = nextReasons;
};

const patchAttachedQuote = (updatedQuote) => {
  if (!ticket.value || !Array.isArray(ticket.value.quotes) || !updatedQuote?.id) {
    return;
  }

  ticket.value.quotes = ticket.value.quotes.map((quote) =>
    quote.id === updatedQuote.id ? { ...quote, ...updatedQuote } : quote,
  );
};

const updateAttachedQuoteStatus = async (quote, payload) => {
  if (!quote?.id || isQuoteActionLoading(quote)) {
    return;
  }

  quoteActionLoading.value = {
    ...quoteActionLoading.value,
    [quote.id]: true,
  };

  try {
    const { data } = await apiClient.put(`/api/quote/${quote.id}`, payload);
    patchAttachedQuote(data?.data || data);
    if (["accepted", "declined"].includes(String(payload.status || "").toLowerCase())) {
      cancelQuoteDecline(quote);
    }
    return true;
  } catch (error) {
    console.error(error);
    alert("Unable to update quote status.");
    return false;
  } finally {
    const next = { ...quoteActionLoading.value };
    delete next[quote.id];
    quoteActionLoading.value = next;
  }
};

const acceptAttachedQuote = (quote) => {
  acceptanceModal.value = {
    quote,
    sendEmail: false,
    personalMessage: "",
  };
};

const confirmAcceptAttachedQuote = async () => {
  const modal = acceptanceModal.value;
  if (!modal?.quote) return;

  const success = await updateAttachedQuoteStatus(modal.quote, {
    status: "accepted",
    send_acceptance_email: Boolean(modal.sendEmail && modal.quote.sent_via_ticket),
    acceptance_message: modal.personalMessage,
  });
  if (success) acceptanceModal.value = null;
};

const declineAttachedQuote = (quote) =>
  selectedQuoteDeclineReason(quote)
    ? updateAttachedQuoteStatus(quote, {
        status: "declined",
        decline_reason: selectedQuoteDeclineReason(quote),
      })
    : null;

const latestConversationItem = computed(() =>
  Array.isArray(ticket.value?.items) && ticket.value.items.length
    ? ticket.value.items[ticket.value.items.length - 1]
    : null,
);

const normalizeCcRecipients = (value) => {
  const values = Array.isArray(value) ? value : [value];
  const recipients = new Map();

  values.forEach((candidate) => {
    if (candidate && typeof candidate === "object") {
      const email = String(candidate.email || "").trim().toLowerCase();
      if (email && email.includes("@") && !recipients.has(email)) {
        recipients.set(email, {
          name: String(candidate.name || "").trim() || null,
          email,
        });
      }
      return;
    }

    const matches = String(candidate || "").match(
      /[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
    ) || [];
    matches.forEach((match) => {
      const email = match.toLowerCase();
      if (!recipients.has(email)) {
        recipients.set(email, { name: null, email });
      }
    });
  });

  return [...recipients.values()];
};

const ccRecipientsForItem = (item) => {
  const storedRecipients = normalizeCcRecipients(item?.email_cc || []);
  if (storedRecipients.length) {
    return storedRecipients;
  }

  return normalizeCcRecipients(item?.raw_payload?.cc || []);
};

const formatEmailRecipient = (recipient) =>
  recipient?.name &&
  String(recipient.name).trim().toLowerCase() !== String(recipient.email || "").trim().toLowerCase()
    ? `${recipient.name} <${recipient.email}>`
    : String(recipient?.email || "");

const isValidEmailAddress = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const rawEmailItems = computed(() => {
  const items = Array.isArray(ticket.value?.items) ? ticket.value.items : [];

  return items.filter((item) => !item?.user_id).slice().reverse();
});

const activeRawEmailItem = computed(() =>
  rawEmailItems.value.find(
    (item) => Number(item.id) === Number(rawEmailItemId.value),
  ) || rawEmailItems.value[0] || null,
);

const latestInboundCcRecipients = computed(() =>
  ccRecipientsForItem(rawEmailItems.value[0]),
);

const spamProviderThreshold = (item) => {
  const report = String(item?.spam_report || "");
  const match = report.match(/[+-]?\d+(?:\.\d+)?\s+points?\s*,\s*([+-]?\d+(?:\.\d+)?)\s+required/i);

  return match?.[1] ?? null;
};

const spamProviderScoreLabel = (item) => {
  const score = item?.spam_score;
  if (score === null || score === undefined || score === "") {
    return "Provider report available";
  }

  return spamProviderThreshold(item)
    ? `Provider score ${score} / ${spamProviderThreshold(item)}`
    : `Provider score ${score}`;
};

const rawEmailPayload = computed(() => {
  if (activeRawEmailItem.value?.raw_payload) {
    return activeRawEmailItem.value.raw_payload;
  }

  const item = activeRawEmailItem.value;
  if (!item) {
    return {};
  }

  return {
    _source: "derived_from_ticket_item",
    _note: "The original provider payload was not retained for this older message.",
    from: item.contact?.email || null,
    from_name: item.contact?.name || null,
    to: item.email_to || null,
    cc: item.email_cc || [],
    text: item.text || null,
    html: item.html || null,
    sender_ip: item.sender_ip || null,
    spam_report: item.spam_report || null,
    spam_score: item.spam_score ?? null,
    created_at: item.created_at || null,
  };
});

const formattedRawEmailPayload = computed(() =>
  JSON.stringify(rawEmailPayload.value, null, 2),
);

const openInfoModal = async (tab = "activity") => {
  infoTab.value = tab === "raw" && rawEmailItems.value.length ? "raw" : "activity";
  if (!rawEmailItemId.value && rawEmailItems.value.length) {
    rawEmailItemId.value = rawEmailItems.value[0].id;
  }
  infoOpen.value = true;

  if (infoTab.value === "activity") {
    await loadTicketHistory();
  }
};

const closeInfoModal = () => {
  infoOpen.value = false;
};

const selectInfoTab = async (tab) => {
  if (tab === "raw" && !rawEmailItems.value.length) {
    return;
  }

  infoTab.value = tab;
  if (tab === "activity") {
    await loadTicketHistory();
  }
};

const latestExternalContact = computed(() => {
  const items = Array.isArray(ticket.value?.items) ? ticket.value.items : [];

  return items.slice().reverse().find((item) => item?.contact)?.contact ?? null;
});

const replyRecipientLabel = computed(() =>
  replyRecipient.value
    ? formatEmailRecipient(replyRecipient.value)
    : "Choose a recipient",
);

const replyRecipientCompactLabel = computed(() =>
  String(
    replyRecipient.value?.name || replyRecipient.value?.email || "choose a recipient",
  ).trim(),
);

const replyRecipientInitials = computed(() => {
  const source = String(
    replyRecipient.value?.name || replyRecipient.value?.email || "?",
  ).trim();
  const parts = source.split(/\s+/).filter(Boolean);

  return (parts.length > 1
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`
    : source.slice(0, 2)
  ).toUpperCase();
});

const availableInboundCcRecipients = computed(() => {
  const selected = new Set(
    replyCcRecipients.value.map((recipient) => recipient.email.toLowerCase()),
  );
  const primaryEmail = String(replyRecipient.value?.email || "").toLowerCase();

  return latestInboundCcRecipients.value.filter((recipient) =>
    recipient.email.toLowerCase() !== primaryEmail &&
    !selected.has(recipient.email.toLowerCase()),
  );
});

const replyCcAutocompleteOptions = computed(() => {
  const selected = new Set(
    replyCcRecipients.value.map((recipient) => recipient.email.toLowerCase()),
  );
  const primaryEmail = String(replyRecipient.value?.email || "").toLowerCase();
  const options = replyCcContactSuggestions.value
    .filter((contact) => {
      const email = String(contact?.email || "").trim().toLowerCase();
      return email && email !== primaryEmail && !selected.has(email);
    })
    .map((contact) => ({
      key: `contact-${contact.id}`,
      type: "contact",
      contact,
      name: String(contact.name || "").trim() || null,
      email: String(contact.email || "").trim().toLowerCase(),
    }));

  const typedValue = String(replyCcInput.value || "").trim();
  if (
    isValidEmailAddress(typedValue) &&
    typedValue.toLowerCase() !== primaryEmail &&
    !selected.has(typedValue.toLowerCase()) &&
    !options.some((option) => option.email === typedValue.toLowerCase())
  ) {
    options.push({
      key: `email-${typedValue.toLowerCase()}`,
      type: "email",
      name: null,
      email: typedValue.toLowerCase(),
    });
  }

  return options;
});

const closeReplyCcSuggestions = () => {
  clearTimeout(replyCcSearchTimer);
  replyCcSearchRequest += 1;
  replyCcSuggestionsVisible.value = false;
  replyCcSearchLoading.value = false;
  replyCcSelectedSuggestionIndex.value = -1;
};

const fetchReplyCcContactSuggestions = async (query) => {
  const requestId = ++replyCcSearchRequest;
  replyCcSearchLoading.value = true;

  try {
    const { data } = await apiClient.get(
      `/api/contact/search?limit=8&query=${encodeURIComponent(query)}`,
    );

    if (requestId !== replyCcSearchRequest || query !== String(replyCcInput.value || "").trim()) {
      return;
    }

    replyCcContactSuggestions.value = Array.isArray(data) ? data : [];
    replyCcSuggestionsVisible.value = true;
    replyCcSelectedSuggestionIndex.value = replyCcAutocompleteOptions.value.length ? 0 : -1;
  } catch (error) {
    if (requestId === replyCcSearchRequest) {
      replyCcContactSuggestions.value = [];
    }
  } finally {
    if (requestId === replyCcSearchRequest) {
      replyCcSearchLoading.value = false;
    }
  }
};

const handleReplyCcInput = (event) => {
  replyCcInput.value = event.target.value;
  const query = String(event.target.value || "").trim();
  replyCcError.value = "";
  replyCcContactSuggestions.value = [];
  replyCcSuggestionsVisible.value = query.length >= 2;
  replyCcSelectedSuggestionIndex.value = replyCcAutocompleteOptions.value.length ? 0 : -1;
  clearTimeout(replyCcSearchTimer);
  replyCcSearchRequest += 1;

  if (query.length < 2) {
    replyCcSearchLoading.value = false;
    return;
  }

  replyCcSearchLoading.value = true;
  replyCcSearchTimer = setTimeout(
    () => fetchReplyCcContactSuggestions(query),
    180,
  );
};

const addReplyCc = (value = replyCcInput.value) => {
  const primaryEmail = String(replyRecipient.value?.email || "").toLowerCase();
  const normalizedRecipients = normalizeCcRecipients(value);
  const recipients = normalizedRecipients.filter(
    (recipient) => recipient.email.toLowerCase() !== primaryEmail,
  );
  if (!recipients.length) {
    replyCcError.value = normalizedRecipients.length
      ? "That address is already the primary recipient."
      : "Enter a valid email address.";
    return false;
  }

  const merged = normalizeCcRecipients([
    ...replyCcRecipients.value,
    ...recipients,
  ]);
  if (merged.length > 20) {
    replyCcError.value = "You can CC up to 20 people.";
    return false;
  }

  replyCcRecipients.value = merged;
  replyCcInput.value = "";
  replyCcError.value = "";
  replyCcContactSuggestions.value = [];
  closeReplyCcSuggestions();
  return true;
};

const removeReplyCc = (email) => {
  replyCcRecipients.value = replyCcRecipients.value.filter(
    (recipient) => recipient.email !== email,
  );
  replyCcError.value = "";
};

const editReplyRecipient = () => {
  replyRecipientControlsOpen.value = true;
  replyRecipientQuery.value = "";
  replyRecipientEditing.value = true;
};

const openReplyRecipientControls = () => {
  replyRecipientControlsOpen.value = true;
};

const cancelReplyRecipientEdit = () => {
  replyRecipientEditing.value = false;
  replyRecipientQuery.value = "";
};

const selectReplyRecipient = (contact) => {
  replyRecipient.value = contact;
  replyRecipientEditing.value = false;
  replyRecipientQuery.value = "";
  replyCcRecipients.value = replyCcRecipients.value.filter(
    (recipient) => recipient.email !== String(contact?.email || "").toLowerCase(),
  );
};

const openReplyCc = async () => {
  replyRecipientControlsOpen.value = true;
  replyCcVisible.value = true;
  await nextTick();
  replyCcInputRef.value?.focus();
};

const handleReplyCcKeydown = (event) => {
  const options = replyCcAutocompleteOptions.value;

  if (event.key === "ArrowDown" && options.length) {
    event.preventDefault();
    replyCcSuggestionsVisible.value = true;
    replyCcSelectedSuggestionIndex.value = Math.min(
      replyCcSelectedSuggestionIndex.value + 1,
      options.length - 1,
    );
    return;
  }

  if (event.key === "ArrowUp" && options.length) {
    event.preventDefault();
    replyCcSelectedSuggestionIndex.value = Math.max(
      replyCcSelectedSuggestionIndex.value - 1,
      0,
    );
    return;
  }

  if (event.key === "Escape") {
    closeReplyCcSuggestions();
    return;
  }

  if (event.key === "Backspace" && !replyCcInput.value && replyCcRecipients.value.length) {
    removeReplyCc(replyCcRecipients.value[replyCcRecipients.value.length - 1].email);
    return;
  }

  const selectedOption = options[replyCcSelectedSuggestionIndex.value];
  if (
    (event.key === "Enter" || event.key === "Tab") &&
    replyCcSuggestionsVisible.value &&
    selectedOption
  ) {
    event.preventDefault();
    addReplyCc(selectedOption);
    return;
  }

  const commitsTypedAddress =
    ["Enter", ",", ";"].includes(event.key) ||
    (event.key === " " && isValidEmailAddress(replyCcInput.value));
  if (!commitsTypedAddress) {
    return;
  }

  event.preventDefault();
  if (String(replyCcInput.value || "").trim()) {
    addReplyCc();
  }
};

const selectReplyCcAutocompleteOption = (option) => {
  addReplyCc(option);
};

const handleReplyCcBlur = () => {
  if (normalizeCcRecipients(replyCcInput.value).length) {
    addReplyCc();
    return;
  }

  closeReplyCcSuggestions();
};

const handleReplyComposerKeydown = (event) => {
  if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "c") {
    event.preventDefault();
    openReplyCc();
  }
};

const contactSearchResultOutput = (contact) => {
  const name = String(contact?.name || "").trim();
  const email = String(contact?.email || "").trim();

  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} <${email}>`;
  }

  return name || email || "";
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ticketPreview = computed(() => {
  const latestItem = latestConversationItem.value;
  const preview = String(latestItem?.text || stripHtml(latestItem?.html))
    .replace(/\s+/g, " ")
    .trim();

  if (!preview) {
    return "No message preview available yet.";
  }

  return preview.length > 200 ? `${preview.slice(0, 197)}...` : preview;
});

const ticketCounterparty = computed(() => {
  const latestItem = latestConversationItem.value;

  return (
    latestItem?.contact?.name ||
    latestItem?.user?.name ||
    latestItem?.recipient?.name ||
    "Unknown contact"
  );
});

const ticketChannelMeta = computed(() => {
  const channel = String(ticket.value?.channel || "").trim().toLowerCase();

  switch (channel) {
    case "events":
      return { label: "Event", classes: "bg-fuchsia-100 text-fuchsia-800" };
    case "transport":
      return { label: "Transport", classes: "bg-sky-100 text-sky-800" };
    case "vacancies":
    case "job":
      return { label: "Job", classes: "bg-emerald-100 text-emerald-800" };
    case "finance":
    case "invoices":
    case "payroll":
      return { label: "Finance", classes: "bg-amber-100 text-amber-900" };
    case "junk":
      return { label: "Junk", classes: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" };
    default:
      return { label: "General", classes: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" };
  }
});

const ticketStatusPills = computed(() => {
  const pills = [];

  if (ticket.value?.spam_marked_at) {
    return [{
      key: "spam",
      label: ticket.value?.spam_source === "automatic" ? "Automatic spam" : "Spam",
      classes: "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-100",
    }];
  }

  if (ticket.value?.awaitingResponse) {
    pills.push({
      key: "awaiting",
      label: "Needs reply",
      classes: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/60 dark:text-cyan-100",
    });
  } else {
    pills.push({
      key: "read",
      label: "Read",
      classes: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100",
    });
  }

  if (ticket.value?.flagged) {
    pills.push({
      key: "flagged",
      label: "Flagged",
      classes: "bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100",
    });
  }

  if (ticket.value?.draft) {
    pills.push({
      key: "draft",
      label: "Draft ready",
      classes: "bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-100",
    });
  }

  return pills;
});

const ticketSummaryStats = computed(() => {
  const stats = [
    {
      label: "Latest correspondent",
      value: ticketCounterparty.value,
    },
    {
      label: "Opened",
      value: ticket.value?.created_at ? dayjs(ticket.value.created_at).format("D MMM YYYY, HH:mm") : "-",
    },
    {
      label: "Updated",
      value: ticket.value?.updated_at ? dayjs(ticket.value.updated_at).format("D MMM YYYY, HH:mm") : "-",
    },
  ];

  if (ticket.value?.spam_marked_at) {
    stats.push({
      label: "Spam source",
      value: ticket.value.spam_source === "automatic" ? "Automatic" : "Manual",
    });
    stats.push({
      label: "Deletes after",
      value: ticket.value?.spam_purge_blocked_at
        ? "Blocked — review required"
        : ticket.value?.spam_purge_at
        ? dayjs(ticket.value.spam_purge_at).format("D MMM YYYY, HH:mm")
        : "Pending",
    });
  }

  return stats;
});

const selectedAttachmentNames = computed(() =>
  attachments.value.map((attachment) => attachment.name),
);

const internalActivityEntries = computed(() =>
  historyEntries.value
    .filter((entry) => internalHistoryEvents.has(entry?.event))
    .slice(0, 4),
);

const closeActionMenu = () => {
  actionMenuOpen.value = false;
};

const toggleActionMenu = () => {
  actionMenuOpen.value = !actionMenuOpen.value;
};

const refreshTicketLists = () => {
  ticketStore.fetchUnansweredCount({ force: true });
  ticketStore.requestListRefresh();
};

const toggleFlag = async () => {
  const actionTicketId = ticket.value.id;
  const nextFlagged = !ticket.value.flagged;
  ticket.value.flagged = nextFlagged;

  try {
    const response = await apiClient.put(`/api/ticket/${actionTicketId}/flag`, {
      flagged: nextFlagged,
    });
    ticket.value.flagged = Boolean(response.data?.flagged_at);
    refreshTicketLists();
    undoToast.show({
      message: nextFlagged ? "Ticket flagged." : "Flag removed.",
      undoMessage: nextFlagged ? "Flag removed." : "Ticket flagged again.",
      onUndo: async () => {
        const undoResponse = await apiClient.put(
          `/api/ticket/${actionTicketId}/flag`,
          { flagged: !nextFlagged },
        );
        if (ticket.value?.id === actionTicketId) {
          ticket.value.flagged = Boolean(undoResponse.data?.flagged_at);
        }
        refreshTicketLists();
      },
    });
  } catch (error) {
    ticket.value.flagged = !nextFlagged;
    console.error(error);
  }
};

const deleteTicket = async () => {
  if (!confirm("Are you sure you want to delete?")) {
    return;
  }

  const actionTicketId = ticket.value.id;
  await apiClient.delete(`/api/ticket/${actionTicketId}`);
  refreshTicketLists();
  await router.push(backToListRoute.value);
  undoToast.show({
    message: "Ticket deleted.",
    undoMessage: "Ticket restored.",
    onUndo: async () => {
      await apiClient.post(`/api/ticket/${actionTicketId}/restore`);
      refreshTicketLists();
    },
  });
};

const markTicketAsSpam = async () => {
  if (!ticket.value?.id || updatingSpam.value) {
    return;
  }

  const reason = window.prompt(
    "Move this ticket to Spam? It will be permanently deleted after 30 days. Add an optional reason:",
    "",
  );
  if (reason === null) {
    return;
  }

  updatingSpam.value = true;
  try {
    const actionTicketId = ticket.value.id;
    const { data } = await apiClient.post(
      `/api/ticket/${actionTicketId}/spam`,
      {
        reason: reason.trim() || null,
      },
    );
    ticket.value = data.ticket;
    if (data.history) {
      historyEntries.value = [
        data.history,
        ...historyEntries.value.filter((entry) => entry.id !== data.history.id),
      ];
    }
    refreshTicketLists();
    await router.push(backToListRoute.value);
    undoToast.show({
      message: "Ticket moved to Spam.",
      undoMessage: "Ticket restored from Spam.",
      onUndo: async () => {
        await apiClient.post(`/api/ticket/${actionTicketId}/not-spam`, {
          reason: "Undo: ticket marked as spam",
        });
        refreshTicketLists();
      },
    });
  } catch (error) {
    if (auth.isLoggedIn) {
      alert(apiErrorMessage(error, "Unable to mark this ticket as spam."));
    }
  } finally {
    updatingSpam.value = false;
  }
};

const restoreTicketFromSpam = async () => {
  if (!ticket.value?.id || updatingSpam.value) {
    return;
  }

  const reason = window.prompt(
    "Restore this ticket to its previous section? Add an optional reason:",
    "",
  );
  if (reason === null) {
    return;
  }

  updatingSpam.value = true;
  try {
    const actionTicketId = ticket.value.id;
    const { data } = await apiClient.post(
      `/api/ticket/${actionTicketId}/not-spam`,
      {
        reason: reason.trim() || null,
      },
    );
    ticket.value = data.ticket;
    if (data.history) {
      historyEntries.value = [
        data.history,
        ...historyEntries.value.filter((entry) => entry.id !== data.history.id),
      ];
    }
    refreshTicketLists();
    undoToast.show({
      message: "Ticket restored from Spam.",
      undoMessage: "Ticket moved back to Spam.",
      onUndo: async () => {
        const undoResponse = await apiClient.post(
          `/api/ticket/${actionTicketId}/spam`,
          {
            reason: "Undo: ticket restored from spam",
          },
        );
        if (ticket.value?.id === actionTicketId) {
          ticket.value = undoResponse.data.ticket;
        }
        refreshTicketLists();
      },
    });
  } catch (error) {
    if (auth.isLoggedIn) {
      alert(apiErrorMessage(error, "Unable to restore this ticket from spam."));
    }
  } finally {
    updatingSpam.value = false;
  }
};

const closeTicket = async () => {
  if (!confirm("Close this ticket? It will move to the Closed section.")) {
    return;
  }

  const actionTicketId = ticket.value.id;
  const response = await apiClient.post(`/api/ticket/${actionTicketId}/close`);
  ticket.value.closed_at =
    response.data?.closed_at ||
    response.data?.flagged_at ||
    new Date().toISOString();
  refreshTicketLists();
  await router.push(backToListRoute.value);
  undoToast.show({
    message: "Ticket closed.",
    undoMessage: "Ticket reopened.",
    onUndo: async () => {
      await apiClient.post(`/api/ticket/${actionTicketId}/reopen`);
      refreshTicketLists();
    },
  });
};

const filenameFromDisposition = (value, fallback) => {
  const disposition = String(value || "");
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/^"|"$/g, ""));
  }

  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

const exportEvidencePackage = async () => {
  if (!ticket.value?.id || exportingEvidence.value) {
    return;
  }

  exportingEvidence.value = true;
  try {
    const response = await apiClient.get(
      `/api/ticket/${ticket.value.id}/evidence-export`,
      {
        responseType: "blob",
        headers: { Accept: "application/zip" },
      },
    );
    const filename = filenameFromDisposition(
      response.headers?.["content-disposition"],
      `ticket-${ticket.value.id}-evidence.zip`,
    );
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    alert(apiErrorMessage(error, "Unable to export the evidence package."));
  } finally {
    exportingEvidence.value = false;
  }
};

const ticketActionItems = computed(() => {
  if (ticket.value?.spam_marked_at) {
    const spamItems = [];
    if (canExportEvidence.value) {
      spamItems.push({
        key: "evidence-export",
        label: exportingEvidence.value
          ? "Exporting Evidence Package..."
          : "Export Evidence Package",
        icon: ArrowDownOnSquareIcon,
        classes: "text-slate-700 dark:text-slate-200",
        disabled: exportingEvidence.value,
        action: exportEvidencePackage,
      });
    }
    spamItems.push({
      key: "not-spam",
      label: updatingSpam.value ? "Restoring..." : "Not Spam / Restore",
      icon: ArrowUturnLeftIcon,
      classes: "text-emerald-700 dark:text-emerald-300",
      disabled: updatingSpam.value,
      action: restoreTicketFromSpam,
    });

    return spamItems;
  }

  const items = [
    {
      key: "log-call",
      label: "Log Call",
      icon: PhoneIcon,
      classes: "text-cyan-700 dark:text-cyan-300",
      disabled: loggingCall.value,
      action: openCallLog,
    },
    {
      key: "quote",
      label: "Create Quote",
      icon: DocumentTextIcon,
      classes: "text-cyan-700 dark:text-cyan-300",
      disabled: false,
      action: createQuote,
    },
  ];

  if (canSeePurchaseInvoiceAction.value) {
    const remainingAttachments = unfiledPurchaseInvoiceAttachments.value.length;
    items.push({
      key: "supplier-invoices",
      label: !canFilePurchaseInvoices.value
        ? "Send to Supplier Invoices (editor access required)"
        : transferringTicketInvoices.value
        ? "Sending to Supplier Invoices..."
        : remainingAttachments
          ? `Send to Supplier Invoices (${remainingAttachments})`
          : "Already in Supplier Invoices",
      icon: DocumentTextIcon,
      classes: canFilePurchaseInvoices.value && remainingAttachments
        ? "text-cyan-700 dark:text-cyan-300"
        : "text-slate-500 dark:text-slate-400",
      disabled:
        !canFilePurchaseInvoices.value ||
        transferringTicketInvoices.value ||
        remainingAttachments === 0,
      action: transferTicketToSupplierInvoices,
    });
  }

  if (canTransferPayrollTimesheets.value) {
    items.push({
      key: "payroll-timesheets",
      label: "Send Timesheets to Payroll",
      icon: ClockIcon,
      classes: "text-violet-700 dark:text-violet-300",
      disabled: false,
      action: openPayrollTransferModal,
    });
  }

  items.push({
    key: "job-application",
    label: creatingVacancy.value
      ? "Converting to Job Application..."
      : "Convert to Job Application",
    icon: PlusCircleIcon,
    classes: "text-cyan-700 dark:text-cyan-300",
    disabled: creatingVacancy.value,
    action: createVacancy,
  });

  items.push({
    key: ticket.value?.awaitingResponse ? "read" : "unread",
    label: ticket.value?.awaitingResponse ? "Mark as Read" : "Mark as Unread",
    icon: ArchiveBoxIcon,
    classes: "text-slate-700 dark:text-slate-200",
    disabled: false,
    action: ticket.value?.awaitingResponse ? markRead : markUnread,
  });

  if (ticket.value?.id) {
    items.push({
      key: "flag",
      label: ticket.value?.flagged ? "Unflag Ticket" : "Flag Ticket",
      icon: FlagIcon,
      classes: "text-slate-700 dark:text-slate-200",
      disabled: false,
      action: toggleFlag,
    });
    if (!ticket.value?.closed_at) {
      items.push({
        key: "close",
        label: "Close Ticket",
        icon: FolderArrowDownIcon,
        classes: "text-slate-700 dark:text-slate-200",
        disabled: false,
        action: closeTicket,
      });
    }
    if (canExportEvidence.value) {
      items.push({
        key: "evidence-export",
        label: exportingEvidence.value
          ? "Exporting Evidence Package..."
          : "Export Evidence Package",
        icon: ArrowDownOnSquareIcon,
        classes: "text-slate-700 dark:text-slate-200",
        disabled: exportingEvidence.value,
        action: exportEvidencePackage,
      });
    }
    items.push({
      key: "spam",
      label: updatingSpam.value ? "Moving to Spam..." : "Mark as Spam",
      icon: ShieldExclamationIcon,
      classes: "text-rose-700 dark:text-rose-300",
      disabled: updatingSpam.value,
      action: markTicketAsSpam,
    });
    items.push({
      key: "delete",
      label: "Delete Ticket",
      icon: TrashIcon,
      classes: "text-rose-700",
      disabled: false,
      action: deleteTicket,
    });
  }

  return items;
});

const ticketActionDescriptions = {
  "log-call": "Add a phone call to the ticket activity.",
  quote: "Start a quote from this conversation.",
  "supplier-invoices": "File attachments for finance extraction.",
  "payroll-timesheets": "Import attachments into the current pay period.",
  "job-application": "Create a vacancy application from this ticket.",
  read: "Remove this ticket from needs reply.",
  unread: "Return this ticket to needs reply.",
  flag: "Toggle the follow-up flag for this ticket.",
  close: "Move this conversation into closed tickets.",
  "not-spam": "Restore this ticket to the normal inbox.",
  "evidence-export": "Download the full ticket audit package.",
  spam: "Move this ticket into spam review.",
  delete: "Soft-delete this ticket from the inbox.",
};

const ticketActionGroups = computed(() => {
  const definitions = [
    {
      key: "create",
      label: "Create & transfer",
      keys: ["log-call", "quote", "supplier-invoices", "payroll-timesheets", "job-application"],
    },
    {
      key: "manage",
      label: "Manage ticket",
      keys: ["read", "unread", "flag", "close", "not-spam"],
    },
    {
      key: "records",
      label: "Records",
      keys: ["evidence-export"],
    },
    {
      key: "danger",
      label: "Review & remove",
      keys: ["spam", "delete"],
    },
  ];

  return definitions
    .map((group) => ({
      ...group,
      items: ticketActionItems.value
        .filter((item) => group.keys.includes(item.key))
        .map((item) => ({
          ...item,
          description: ticketActionDescriptions[item.key] || "",
        })),
    }))
    .filter((group) => group.items.length > 0);
});

const runActionItem = async (item) => {
  if (item.disabled || activeTicketAction.value) {
    return;
  }

  closeActionMenu();
  activeTicketAction.value = item.key;
  try {
    await item.action();
  } catch (error) {
    if (auth.isLoggedIn) {
      alert(apiErrorMessage(error, `Unable to ${item.label.toLowerCase()}.`));
    }
  } finally {
    if (activeTicketAction.value === item.key) {
      activeTicketAction.value = null;
    }
  }
};

const handleDocumentClick = (event) => {
  if (actionMenuRef.value?.contains(event.target)) {
    return;
  }

  closeActionMenu();
};

const markRead = async () => {
  const actionTicketId = ticket.value.id;
  await apiClient.put(`/api/ticket/${actionTicketId}/read`);
  ticket.value.awaitingResponse = false;
  refreshTicketLists();
  await router.push(backToListRoute.value);
  undoToast.show({
    message: "Ticket marked as read.",
    undoMessage: "Ticket marked as unread.",
    onUndo: async () => {
      await apiClient.put(`/api/ticket/${actionTicketId}/unread`);
      refreshTicketLists();
    },
  });
};

const markUnread = async () => {
  const actionTicketId = ticket.value.id;
  await apiClient.put(`/api/ticket/${actionTicketId}/unread`);
  ticket.value.awaitingResponse = true;
  refreshTicketLists();
  await router.push(backToListRoute.value);
  undoToast.show({
    message: "Ticket marked as unread.",
    undoMessage: "Ticket marked as read.",
    onUndo: async () => {
      await apiClient.put(`/api/ticket/${actionTicketId}/read`);
      refreshTicketLists();
    },
  });
};

const resetCallLogForm = () => {
  callLogContact.value = null;
  callLogContactQuery.value = "";
  callLogForm.value = {
    direction: "outbound",
    phone: "",
    note: "",
  };
};

const openCallLog = () => {
  const contact = latestExternalContact.value;
  callLogContact.value = contact;
  callLogContactQuery.value = contactSearchResultOutput(contact);

  callLogForm.value = {
    ...callLogForm.value,
    phone: callLogForm.value.phone || contact?.phone || "",
  };
  callLogOpen.value = true;
};

const closeCallLog = () => {
  if (!loggingCall.value) {
    callLogOpen.value = false;
  }
};

const selectCallLogContact = (contact) => {
  callLogContact.value = contact;
  callLogContactQuery.value = contactSearchResultOutput(contact);
  callLogForm.value.phone = contact?.phone || callLogForm.value.phone || "";
};

watch(callLogContactQuery, (value) => {
  if (
    callLogContact.value &&
    value !== contactSearchResultOutput(callLogContact.value)
  ) {
    callLogContact.value = null;
  }
});

async function logCall() {
  if (loggingCall.value) {
    return;
  }

  const note = String(callLogForm.value.note || "").trim();
  if (note.length < 3) {
    return alert("Call notes are too short.");
  }

  loggingCall.value = true;
  try {
    const { data } = await apiClient.post(
      `/api/ticket/${ticketId.value}/call-log`,
      {
        direction: callLogForm.value.direction || "outbound",
        contact_id: callLogContact.value?.id || null,
        contact_name:
          callLogContact.value?.name || callLogContactQuery.value || null,
        phone: callLogForm.value.phone || callLogContact.value?.phone || null,
        note,
      },
    );

    historyEntries.value = [
      data,
      ...historyEntries.value.filter((entry) => entry.id !== data.id),
    ];
    resetCallLogForm();
    callLogOpen.value = false;
  } catch (error) {
    console.error(error);
  } finally {
    loggingCall.value = false;
  }
}

async function reply() {
  if (sendingReply.value) {
    return;
  }

  if (responseText.value === null || responseText.value.trim() === "") {
    return alert("Your message is too short.");
  }

  if (!replyRecipient.value?.id || !String(replyRecipient.value?.email || "").trim()) {
    return alert("Choose a recipient with an email address before sending.");
  }

  if (String(replyCcInput.value || "").trim() && !addReplyCc()) {
    return;
  }

  const formData = new FormData();
  formData.append("ticket_id", ticketId.value);
  formData.append("text", responseText.value);
  formData.append("sendAnonymously", sendAnonymously.value);
  formData.append("close_ticket", closeAfterReply.value ? "1" : "0");
  if (replyRecipient.value?.id) {
    formData.append("recipient_id", replyRecipient.value.id);
  }
  replyCcRecipients.value.forEach((recipient, index) => {
    formData.append(`cc[${index}]`, recipient.email);
  });

  if (attachments.value.length > 0) {
    attachments.value.forEach((attachment, index) => {
      formData.append(`attachments[${index}]`, attachment);
    });
  }

  sendingReply.value = true;
  await nextTick();

  try {
    const r = await apiClient.post(
      "/api/ticket/" + ticketId.value + "/reply",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    ticket.value.items.push(r.data);
    if (infoOpen.value && infoTab.value === "activity") {
      await loadTicketHistory();
    }
    ticket.value.closed_at = closeAfterReply.value
      ? new Date().toISOString()
      : null;
    responseText.value = null;
    replyCcInput.value = "";
    replyCcRecipients.value = [];
    replyCcError.value = "";
    closeAfterReply.value = false;
    responseInput.value.focus();
    ticketStore.fetchUnansweredCount({ force: true });
    router.push(backToListRoute.value);
  } catch (error) {
    console.error(error);
  } finally {
    sendingReply.value = false;
  }
}

const openSteerModal = async () => {
  if (!String(responseText.value || "").trim()) {
    return;
  }

  steerPrompt.value = "";
  steerAttachments.value = [];
  steerError.value = "";
  steerOpen.value = true;
  await nextTick();
  steerPromptInput.value?.focus();
};

const closeSteerModal = () => {
  if (steeringReply.value) {
    return;
  }

  steerOpen.value = false;
  steerPrompt.value = "";
  steerAttachments.value = [];
  if (steerFileInput.value) {
    steerFileInput.value.value = "";
  }
  steerError.value = "";
};

const openSteerFilePicker = () => {
  steerFileInput.value?.click();
};

const onSteerFileChange = (event) => {
  const selectedFiles = [...(event.target.files || [])];
  const combinedFiles = [...steerAttachments.value, ...selectedFiles];
  const uniqueFiles = combinedFiles.filter(
    (file, index, files) =>
      files.findIndex(
        (candidate) =>
          candidate.name === file.name &&
          candidate.size === file.size &&
          candidate.lastModified === file.lastModified,
      ) === index,
  );

  if (uniqueFiles.length > 6) {
    steerError.value = "You can send up to six reference files to AI.";
    event.target.value = "";
    return;
  }

  const oversizedFile = uniqueFiles.find((file) => file.size > 4 * 1024 * 1024);
  if (oversizedFile) {
    steerError.value = `${oversizedFile.name} is larger than the 4 MB limit.`;
    event.target.value = "";
    return;
  }

  steerAttachments.value = uniqueFiles;
  steerError.value = "";
  event.target.value = "";
};

const removeSteerAttachment = (index) => {
  steerAttachments.value = steerAttachments.value.filter(
    (_, attachmentIndex) => attachmentIndex !== index,
  );
};

const formatSteerAttachmentSize = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const steerReply = async () => {
  const instructions = String(steerPrompt.value || "").trim();
  if (!instructions) {
    steerError.value = "Tell AI how you would like the reply changed.";
    return;
  }

  steeringReply.value = true;
  steerError.value = "";

  try {
    const formData = new FormData();
    formData.append("draft", responseText.value);
    formData.append("instructions", instructions);
    steerAttachments.value.forEach((attachment, index) => {
      formData.append(`attachments[${index}]`, attachment);
    });

    const { data } = await apiClient.post(
      `/api/ticket/${ticketId.value}/reply/steer`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        suppressErrorAlert: true,
      },
    );

    const rewrittenReply = String(data?.reply || "").trim();
    if (!rewrittenReply) {
      throw new Error("AI returned an empty reply.");
    }

    responseText.value = rewrittenReply;
    steerOpen.value = false;
    steerPrompt.value = "";
    steerAttachments.value = [];
    await nextTick();
    responseInput.value?.focus();
  } catch (error) {
    console.error(error);
    steerError.value = apiErrorMessage(
      error,
      "Unable to rewrite the reply. Your draft has not been changed.",
    );
  } finally {
    steeringReply.value = false;
  }
};

const createQuote = () => {
  if (!ticketId.value) return;
  router.push({
    path: "/quotes/create",
    query: { ticket_id: ticketId.value },
  });
};

const createVacancy = async () => {
  if (!ticketId.value || creatingVacancy.value) {
    return;
  }

  if (
    !confirm(
      "Convert this ticket into a job application? This will create or update an application record and may mark the ticket as read.",
    )
  ) {
    return;
  }

  creatingVacancy.value = true;
  startVacancyProgress();
  try {
    const { data } = await apiClient.post(
      `/api/ticket/${ticketId.value}/vacancy`,
    );
    const vacancyId = data?.vacancy?.id;

    if (!vacancyId) {
      alert("Job application was created but could not be opened.");
      return;
    }

    ticketStore.fetchUnansweredCount({ force: true });
    vacancyStore.fetchNewCount({ force: true });
    completeVacancyProgress();
    router.push(`/vacancies/${vacancyId}`);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      "Unable to convert this ticket into a job application.";
    alert(message);
  } finally {
    stopVacancyProgress();
    creatingVacancy.value = false;
  }
};

const updateVacancyProgressLabel = () => {
  if (vacancyProgress.value < 35) {
    vacancyProgressLabel.value = "Reading ticket";
    return;
  }

  if (vacancyProgress.value < 70) {
    vacancyProgressLabel.value = "Extracting applicant details";
    return;
  }

  if (vacancyProgress.value < 100) {
    vacancyProgressLabel.value = "Creating job application";
    return;
  }

  vacancyProgressLabel.value = "Opening application";
};

const startVacancyProgress = () => {
  vacancyProgress.value = 8;
  updateVacancyProgressLabel();

  if (vacancyProgressTimer) {
    clearInterval(vacancyProgressTimer);
  }

  vacancyProgressTimer = setInterval(() => {
    if (vacancyProgress.value >= 92) {
      return;
    }

    vacancyProgress.value = Math.min(
      92,
      vacancyProgress.value + Math.max(2, Math.floor((100 - vacancyProgress.value) / 14)),
    );
    updateVacancyProgressLabel();
  }, 180);
};

const completeVacancyProgress = () => {
  vacancyProgress.value = 100;
  updateVacancyProgressLabel();
};

const stopVacancyProgress = () => {
  if (vacancyProgressTimer) {
    clearInterval(vacancyProgressTimer);
    vacancyProgressTimer = null;
  }
};

const getDeliveryStatus = (item) => {
  const email = item?.latest_email;
  if (!email) return null;
  if ((email.opens ?? 0) > 0 || email.is_read) {
    return { label: "Opened", classes: "bg-emerald-100 text-emerald-800" };
  }
  if (email.delivered) {
    return { label: "Delivered", classes: "bg-sky-100 text-sky-800" };
  }
  return { label: "Sent", classes: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" };
};

const formatHistoryEvent = (event) =>
  String(event || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatCallDirection = (direction) =>
  String(direction || "").toLowerCase() === "inbound"
    ? "Inbound call"
    : "Outbound call";

const historyNote = (entry) => String(entry?.metadata?.note || entry?.metadata?.reason || "").trim();

const historyDetail = (entry) => {
  const metadata = entry?.metadata || {};
  const parts = [];

  if (entry?.event === "phone_call_logged") {
    parts.push(formatCallDirection(metadata.direction));
  }

  if (entry?.event === "ticket_marked_spam") {
    parts.push(metadata.source === "automatic" ? "Automatic decision" : "Manual decision");
    if (metadata.confidence !== undefined) {
      parts.push(`${Math.round(Number(metadata.confidence) * 100)}% confidence`);
    }
  }

  if (metadata.purge_at) {
    parts.push(`Deletion scheduled ${dayjs(metadata.purge_at).format("D MMM YYYY, HH:mm")}`);
  }

  if (metadata.contact_name) {
    parts.push(metadata.contact_name);
  }

  if (metadata.phone) {
    parts.push(metadata.phone);
  }

  const ccRecipients = normalizeCcRecipients(metadata.cc || []);
  if (ccRecipients.length) {
    parts.push(`CC: ${ccRecipients.map(formatEmailRecipient).join(", ")}`);
  }

  return parts.join(" - ");
};

const loadTicketHistory = async () => {
  if (!ticketId.value) {
    return;
  }

  historyLoading.value = true;
  try {
    const { data } = await apiClient.get(`/api/ticket/${ticketId.value}/history`);
    historyEntries.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
    historyEntries.value = [];
  } finally {
    historyLoading.value = false;
  }
};

onBeforeUnmount(() => {
  clearTimeout(replyCcSearchTimer);
  stopVacancyProgress();
  stopPurchaseInvoicePolling();
  document.removeEventListener("click", handleDocumentClick);
});
</script>

<template>
  <div class="space-y-4">
    <template v-if="ticket">
      <div class="box overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <RouterLink :to="backToListRoute" class="btn">
            <ArrowLeftIcon class="icon" />
            Back to list
          </RouterLink>
          <div class="flex flex-wrap gap-2">
            <span
              class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em]"
              :class="ticketChannelMeta.classes"
            >
              {{ ticketChannelMeta.label }}
            </span>
            <span
              v-for="pill in ticketStatusPills"
              :key="pill.key"
              class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em]"
              :class="pill.classes"
            >
              {{ pill.label }}
            </span>
          </div>
        </div>

        <div class="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.7fr)_minmax(18rem,1fr)]">
          <div class="min-w-0">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Ticket #{{ ticket.id }}
            </p>
            <h1 class="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {{ ticket.subject }}
            </h1>
            <p class="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              {{ ticketPreview }}
            </p>
          </div>

          <div class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div
              v-for="stat in ticketSummaryStats"
              :key="stat.label"
              class="flex items-center justify-between gap-4 border-b border-slate-200 py-2 last:border-b-0 last:pb-0 first:pt-0 dark:border-slate-700"
            >
              <span class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                {{ stat.label }}
              </span>
              <span class="text-right text-sm font-semibold text-slate-900 dark:text-slate-100">
                {{ stat.value }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <section
        v-if="ticket.spam_marked_at"
        class="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-rose-900 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-100"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div class="flex items-center gap-2 font-semibold">
              <ShieldExclamationIcon class="h-5 w-5" />
              Quarantined as spam
            </div>
            <p class="mt-1 text-sm leading-6">
              Replies and ticket automations are disabled until this ticket is restored.
            </p>
            <p v-if="ticket.spam_reason" class="mt-1 text-sm">
              Reason: {{ ticket.spam_reason }}
            </p>
          </div>
          <div class="text-right text-xs">
            <div>{{ ticket.spam_source === "automatic" ? "Automatic" : "Manual" }}</div>
            <div v-if="ticket.spam_purge_blocked_at">
              Deletion blocked: {{ ticket.spam_purge_blocked_reason }}
            </div>
            <div v-else-if="ticket.spam_purge_at">
              Deletes {{ dayjs(ticket.spam_purge_at).format("D MMM YYYY, HH:mm") }}
            </div>
          </div>
        </div>
      </section>

      <div v-if="actionBanners.length" class="space-y-3">
        <div
          v-for="action in actionBanners"
          :key="action.id"
          :class="action.classes"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="font-semibold">{{ action.label }}</div>
            <span
              :class="[
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                action.badgeClasses,
              ]"
            >
              {{ action.badge }}
            </span>
          </div>
          <p class="mt-1 leading-6">{{ action.message }}</p>
          <button
            v-if="action.retryable"
            type="button"
            class="btn mt-3 text-xs disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="retryingActionId !== null"
            @click.prevent="retryTicketAction(action)"
          >
            {{ retryingActionId === action.id ? "Retrying..." : "Retry" }}
          </button>
        </div>
      </div>

      <section
        v-if="attachedQuotes.length"
        class="box border-l-4 border-l-cyan-500"
      >
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <DocumentTextIcon class="h-5 w-5 text-cyan-700 dark:text-cyan-300" />
              <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Quote attached
              </h2>
              <span
                v-if="draftAttachedQuotes.length"
                class="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/60 dark:text-amber-100"
              >
                Ready for review
              </span>
            </div>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
              A quote has been prepared from this ticket. Review it before sending a reply.
            </p>
          </div>
          <RouterLink
            v-if="attachedQuotes[0]?.id"
            :to="`/quotes/${attachedQuotes[0].id}?ticket_id=${ticket.id}`"
            class="accent btn inline-flex items-center gap-2 self-start"
          >
            <DocumentTextIcon class="icon" />
            Review Quote
          </RouterLink>
        </div>

        <div class="mt-4 grid gap-3 md:grid-cols-2">
          <div
            v-for="quote in attachedQuotes"
            :key="quote.id"
            class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900/40"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <RouterLink
                :to="`/quotes/${quote.id}?ticket_id=${ticket.id}`"
                class="min-w-0 truncate font-semibold text-cyan-800 hover:text-cyan-950 dark:text-cyan-200 dark:hover:text-cyan-100"
              >
                {{ quote.display_name }}
              </RouterLink>
              <span
                :class="[
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                  quoteStatusClasses(quote.status),
                ]"
              >
                {{ quoteStatusLabel(quote.status) }}
              </span>
            </div>
            <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
              <span v-if="quote.reference_text">{{ quote.reference_text }}</span>
              <span v-if="quote.sent_at">Sent <TimeAgo :data="quote.sent_at" /></span>
              <span v-else-if="quote.updated_at">Updated <TimeAgo :data="quote.updated_at" /></span>
            </div>
            <div
              v-if="canUpdateQuoteStatus(quote)"
              class="mt-3 space-y-2"
            >
              <div class="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  class="inline-flex min-h-[2rem] items-center justify-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400"
                  :disabled="isQuoteActionLoading(quote)"
                  @click="acceptAttachedQuote(quote)"
                >
                  {{ isQuoteActionLoading(quote) ? "Saving..." : "Accept quote" }}
                </button>
                <button
                  v-if="!isQuoteDeclineOpen(quote)"
                  type="button"
                  class="inline-flex min-h-[2rem] items-center justify-center rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-800 dark:bg-slate-950 dark:text-rose-200 dark:hover:bg-rose-950/40"
                  :disabled="isQuoteActionLoading(quote)"
                  @click="showQuoteDeclineReason(quote)"
                >
                  Decline
                </button>
              </div>
              <div
                v-if="isQuoteDeclineOpen(quote)"
                class="flex min-w-0 flex-col gap-2 rounded-lg border border-rose-100 bg-rose-50/70 p-2 dark:border-rose-900/70 dark:bg-rose-950/20 sm:flex-row sm:items-center"
              >
                <select
                  v-model="quoteDeclineReasons[quote.id]"
                  class="min-h-[2rem] min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                  :disabled="isQuoteActionLoading(quote)"
                >
                  <option value="" disabled>- Select -</option>
                  <option
                    v-for="reason in quoteDeclineReasonOptions"
                    :key="reason.value"
                    :value="reason.value"
                  >
                    {{ reason.label }}
                  </option>
                </select>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="inline-flex min-h-[2rem] items-center justify-center rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-rose-500 dark:text-rose-950 dark:hover:bg-rose-400"
                    :disabled="isQuoteActionLoading(quote) || !selectedQuoteDeclineReason(quote)"
                    @click="declineAttachedQuote(quote)"
                  >
                    {{ isQuoteActionLoading(quote) ? "Saving..." : "Save decline" }}
                  </button>
                  <button
                    type="button"
                    class="inline-flex min-h-[2rem] items-center justify-center rounded-md px-2 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:text-slate-400 dark:hover:text-slate-100"
                    :disabled="isQuoteActionLoading(quote)"
                    @click="cancelQuoteDecline(quote)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
            <p
              v-else-if="quote.decline_reason_label"
              class="mt-3 text-xs font-medium text-slate-500 dark:text-slate-400"
            >
              Declined: {{ quote.decline_reason_label }}
            </p>
          </div>
        </div>
      </section>

      <div
        v-if="callLogOpen"
        class="box border-l-4 border-l-cyan-500"
      >
        <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Log Call</h2>
            <span class="mt-1 inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              Internal only
            </span>
          </div>
          <button
            type="button"
            class="btn text-xs"
            :disabled="loggingCall"
            @click.prevent="closeCallLog"
          >
            Cancel
          </button>
        </div>

        <div class="grid gap-4 md:grid-cols-3">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Direction
            <select
              v-model="callLogForm.direction"
              class="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              :disabled="loggingCall"
            >
              <option value="outbound">Outbound</option>
              <option value="inbound">Inbound</option>
            </select>
          </label>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Contact
            <SearchableInput
              class="mt-1"
              v-model="callLogContactQuery"
              placeholder="Search contacts..."
              url="/api/contact/search?query="
              :output="contactSearchResultOutput"
              :clearOnSelect="false"
              :autoShowResults="false"
              @selected="selectCallLogContact"
            />
          </label>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Phone
            <input
              v-model="callLogForm.phone"
              type="text"
              class="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              :disabled="loggingCall"
            />
          </label>
        </div>

        <label class="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
          Call notes
          <textarea
            v-model="callLogForm.note"
            class="mt-1 h-36 w-full rounded-2xl border border-slate-300 bg-white p-3 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            :disabled="loggingCall"
          ></textarea>
        </label>

        <div class="mt-4 flex justify-end">
          <button
            type="button"
            class="accent btn inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="loggingCall"
            @click.prevent="logCall"
          >
            <span
              v-if="loggingCall"
              class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
            ></span>
            {{ loggingCall ? "Saving..." : "Save call log" }}
          </button>
        </div>
      </div>

      <section
        v-if="internalActivityEntries.length"
        class="box"
      >
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Internal Activity</h2>
          <button class="btn text-xs" @click.prevent="openInfoModal('activity')">
            View all activity
          </button>
        </div>
        <div class="divide-y divide-slate-200 dark:divide-slate-700">
          <div
            v-for="entry in internalActivityEntries"
            :key="entry.id"
            class="py-3 first:pt-0 last:pb-0"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="font-medium text-slate-900 dark:text-slate-100">{{ entry.summary }}</div>
              <span class="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                {{ formatHistoryEvent(entry.event) }}
              </span>
            </div>
            <div class="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>{{ entry.user?.name || "Neptune System" }}</span>
              <TimeAgo :data="entry.occurred_at || entry.created_at" />
            </div>
            <div
              v-if="historyDetail(entry)"
              class="mt-2 text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ historyDetail(entry) }}
            </div>
            <p
              v-if="historyNote(entry)"
              class="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 dark:text-slate-200"
            >
              {{ historyNote(entry) }}
            </p>
          </div>
        </div>
      </section>

      <div class="box" @keydown="handleReplyComposerKeydown">
        <div class="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Reply</h2>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              Draft and send a response without leaving the thread.
            </p>
          </div>
          <div class="relative flex flex-1 flex-wrap justify-start gap-2 lg:justify-end" ref="actionMenuRef">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              title="View ticket activity, raw email, and spam analysis"
              @click.prevent="openInfoModal('activity')"
            >
              <InformationCircleIcon class="h-4 w-4" />
              <span>Info</span>
            </button>
            <button
              v-if="!ticket.spam_marked_at"
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700 shadow-sm transition hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/50"
              :disabled="sendingReply || steeringReply || !String(responseText || '').trim()"
              title="Rewrite the current draft using your instructions"
              @click.prevent="openSteerModal"
            >
              <SparklesIcon class="h-4 w-4" />
              <span>AI Steer</span>
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-700 shadow-sm hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-wait disabled:opacity-60 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200 dark:hover:bg-cyan-900/50"
              :aria-expanded="actionMenuOpen"
              :aria-busy="Boolean(activeTicketAction)"
              aria-haspopup="menu"
              aria-label="Open actions menu"
              :disabled="Boolean(activeTicketAction)"
              @click.prevent.stop="toggleActionMenu"
            >
              <span>{{ activeTicketAction ? "Working..." : "Actions" }}</span>
              <ChevronDownIcon class="h-4 w-4" />
            </button>

            <div
              v-if="actionMenuOpen"
              class="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-[1px] lg:hidden"
              aria-hidden="true"
              @click.stop="closeActionMenu"
            ></div>
            <div
              v-if="actionMenuOpen"
              class="fixed inset-x-3 bottom-3 z-50 flex max-h-[min(80vh,42rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800 lg:absolute lg:inset-x-auto lg:bottom-auto lg:right-0 lg:top-full lg:mt-2 lg:w-[42rem]"
              role="menu"
              aria-label="Ticket actions"
            >
              <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                <div>
                  <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">Ticket actions</p>
                  <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Create records, transfer attachments, or manage this ticket.
                  </p>
                </div>
                <button
                  type="button"
                  class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-100 lg:hidden"
                  aria-label="Close ticket actions"
                  @click.stop="closeActionMenu"
                >
                  <XMarkIcon class="h-5 w-5" />
                </button>
              </div>
              <div class="grid flex-1 gap-4 overflow-y-auto p-3 sm:p-4 lg:grid-cols-2">
                <section
                  v-for="group in ticketActionGroups"
                  :key="group.key"
                  :class="group.key === 'danger' ? 'lg:col-span-2' : ''"
                >
                  <h3 class="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                    {{ group.label }}
                  </h3>
                  <div :class="['grid gap-2', group.key === 'danger' ? 'sm:grid-cols-2' : '']">
                    <button
                      v-for="item in group.items"
                      :key="item.key"
                      type="button"
                      class="flex min-h-16 w-full items-start gap-3 rounded-xl border border-slate-200 px-3 py-3 text-left transition hover:border-cyan-200 hover:bg-cyan-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:hover:border-cyan-800 dark:hover:bg-cyan-950/30"
                      :class="item.classes"
                      :disabled="item.disabled || Boolean(activeTicketAction)"
                      role="menuitem"
                      @click.prevent="runActionItem(item)"
                    >
                      <span class="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-900/70">
                        <component
                          v-if="item.icon"
                          :is="item.icon"
                          class="h-4 w-4"
                        />
                      </span>
                      <span class="min-w-0">
                        <span class="block text-sm font-semibold leading-5">{{ item.label }}</span>
                        <span class="mt-0.5 block text-xs leading-4 text-slate-500 dark:text-slate-400">
                          {{ item.description }}
                        </span>
                      </span>
                    </button>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>

        <div v-if="creatingVacancy" class="mb-4 max-w-md">
          <div class="mb-1 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>{{ vacancyProgressLabel }}</span>
            <span>{{ vacancyProgress }}%</span>
          </div>
          <div class="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              class="h-full rounded-full bg-amber-500 transition-all duration-300 ease-out"
              :style="{ width: `${vacancyProgress}%` }"
            ></div>
          </div>
        </div>

        <div class="mb-3 overflow-visible rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/50">
          <div
            v-if="!replyRecipientControlsOpen && !replyCcRecipients.length"
            class="flex min-h-12 items-center px-3 py-2"
          >
            <button
              type="button"
              class="group inline-flex min-w-0 items-center gap-2 rounded-md px-1 py-1 text-left text-sm text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
              :title="`${replyRecipientLabel} — select to edit recipients or add CC`"
              :disabled="sendingReply || ticket.spam_marked_at"
              aria-label="Edit reply recipients and add CC"
              @click.prevent="openReplyRecipientControls"
            >
              <span class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cyan-700 text-[10px] font-semibold text-white dark:bg-cyan-500 dark:text-slate-950">
                {{ replyRecipientInitials }}
              </span>
              <span class="truncate">
                <span class="text-slate-400 dark:text-slate-500">to</span>
                {{ replyRecipientCompactLabel }}
              </span>
              <ChevronDownIcon class="h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:text-slate-600 dark:group-hover:text-slate-200" />
            </button>
          </div>

          <div
            v-else
            class="flex min-h-12 items-center gap-2 px-3 py-2"
          >
            <span class="shrink-0 text-sm font-medium text-slate-500 dark:text-slate-400">
              To
            </span>

            <div v-if="replyRecipientEditing" class="flex min-w-0 flex-1 items-center gap-2">
              <SearchableInput
                class="min-w-0 flex-1"
                v-model="replyRecipientQuery"
                placeholder="Search contacts to change recipient"
                url="/api/contact/search?query="
                :output="contactSearchResultOutput"
                :clearOnSelect="false"
                :autoShowResults="false"
                @selected="selectReplyRecipient"
              />
              <button
                type="button"
                class="shrink-0 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                @click.prevent="cancelReplyRecipientEdit"
              >
                Cancel
              </button>
            </div>

            <button
              v-else
              type="button"
              class="group inline-flex min-w-0 max-w-full items-center gap-2 rounded-full bg-slate-100 py-1 pl-1 pr-2 text-left text-sm text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              :title="`${replyRecipientLabel} — select to change recipient`"
              :disabled="sendingReply || ticket.spam_marked_at"
              @click.prevent="editReplyRecipient"
            >
              <span class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-700 text-[10px] font-semibold text-white dark:bg-cyan-500 dark:text-slate-950">
                {{ replyRecipientInitials }}
              </span>
              <span class="min-w-0 truncate">{{ replyRecipientLabel }}</span>
              <ChevronDownIcon class="h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:text-slate-600 dark:group-hover:text-slate-200" />
            </button>

            <button
              v-if="!replyCcVisible"
              type="button"
              class="ml-auto shrink-0 rounded px-1.5 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-cyan-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-cyan-300"
              :disabled="sendingReply || ticket.spam_marked_at"
              title="Add CC recipients (Ctrl/Command + Shift + C)"
              @click.prevent="openReplyCc"
            >
              Cc
            </button>
          </div>

          <div
            v-if="replyCcVisible || replyCcRecipients.length"
            class="flex min-h-12 items-start gap-2 border-t border-slate-200 px-3 py-2 dark:border-slate-700"
          >
            <span class="mt-1 shrink-0 text-sm font-medium text-slate-500 dark:text-slate-400">Cc</span>
            <div class="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
              <span
                v-for="recipient in replyCcRecipients"
                :key="recipient.email"
                class="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 py-1 pl-2.5 pr-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-100"
              >
                <span class="truncate">{{ formatEmailRecipient(recipient) }}</span>
                <button
                  type="button"
                  class="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-600 dark:hover:text-white"
                  :aria-label="`Remove ${recipient.email} from CC`"
                  :disabled="sendingReply"
                  @click.prevent="removeReplyCc(recipient.email)"
                >
                  <XMarkIcon class="h-3.5 w-3.5" />
                </button>
              </span>
              <div class="relative min-w-[12rem] flex-1">
                <input
                  ref="replyCcInputRef"
                  v-model="replyCcInput"
                  type="text"
                  name="ticket-reply-cc-recipient-entry"
                  role="combobox"
                  aria-label="Add CC recipients"
                  aria-autocomplete="list"
                  :aria-expanded="replyCcSuggestionsVisible"
                  class="w-full border-0 bg-transparent px-1 py-1 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-0 dark:text-slate-100 dark:placeholder:text-slate-500"
                  placeholder="Type a name or email"
                  autocomplete="off"
                  autocapitalize="none"
                  autocorrect="off"
                  spellcheck="false"
                  data-form-type="other"
                  data-bwignore="true"
                  data-lpignore="true"
                  data-1p-ignore
                  :disabled="sendingReply || ticket.spam_marked_at || replyCcRecipients.length >= 20"
                  @input="handleReplyCcInput"
                  @keydown="handleReplyCcKeydown"
                  @blur="handleReplyCcBlur"
                  @focus="replyCcSuggestionsVisible = Boolean(replyCcInput)"
                />

                <div
                  v-if="replyCcSuggestionsVisible && (replyCcAutocompleteOptions.length || replyCcSearchLoading)"
                  class="absolute left-0 top-full z-50 mt-1 max-h-64 w-full min-w-72 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-800"
                  role="listbox"
                  aria-label="CC recipient suggestions"
                >
                  <button
                    v-for="(option, index) in replyCcAutocompleteOptions"
                    :key="option.key"
                    type="button"
                    role="option"
                    :aria-selected="replyCcSelectedSuggestionIndex === index"
                    :class="[
                      'flex w-full items-center gap-3 px-3 py-2 text-left transition',
                      replyCcSelectedSuggestionIndex === index
                        ? 'bg-cyan-50 dark:bg-cyan-950/40'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700',
                    ]"
                    @mousedown.prevent
                    @mouseenter="replyCcSelectedSuggestionIndex = index"
                    @click.prevent="selectReplyCcAutocompleteOption(option)"
                  >
                    <span
                      class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                    >
                      {{ option.type === "contact" ? String(option.name || option.email).slice(0, 2) : "@" }}
                    </span>
                    <span class="min-w-0 flex-1">
                      <span
                        v-if="option.type === 'contact' && option.name && option.name.toLowerCase() !== option.email"
                        class="block truncate text-sm font-medium text-slate-900 dark:text-slate-100"
                      >
                        {{ option.name }}
                      </span>
                      <span class="block truncate text-xs text-slate-500 dark:text-slate-400">
                        {{ option.type === "email" ? `Use ${option.email}` : option.email }}
                      </span>
                    </span>
                  </button>

                  <div
                    v-if="replyCcSearchLoading"
                    class="px-3 py-2 text-xs text-slate-500 dark:text-slate-400"
                  >
                    Searching contacts…
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p v-if="replyCcError" class="border-t border-slate-200 px-3 py-2 text-xs font-medium text-rose-600 dark:border-slate-700 dark:text-rose-300">
            {{ replyCcError }}
          </p>

          <div
            v-if="replyCcVisible && availableInboundCcRecipients.length"
            class="border-t border-slate-200 px-3 py-2 dark:border-slate-700"
          >
            <p class="text-[11px] text-slate-500 dark:text-slate-400">
              CC'd on the latest incoming email
            </p>
            <div class="mt-1.5 flex flex-wrap gap-1.5">
              <button
                v-for="recipient in availableInboundCcRecipients"
                :key="recipient.email"
                type="button"
                class="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 hover:text-slate-900 disabled:opacity-50 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 dark:hover:text-white"
                :disabled="sendingReply || ticket.spam_marked_at"
                @click.prevent="addReplyCc(recipient)"
              >
                <PlusCircleIcon class="h-3.5 w-3.5" />
                {{ formatEmailRecipient(recipient) }}
              </button>
            </div>
          </div>
        </div>

        <textarea
          class="h-40 w-full rounded-2xl border border-slate-300 bg-white p-3 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 md:h-72"
          v-model="responseText"
          ref="responseInput"
          wrap="soft"
          :disabled="sendingReply || ticket.spam_marked_at"
        ></textarea>
        <input
          type="file"
          ref="fileInput"
          class="hidden"
          @change="onFileChange"
          multiple
          :disabled="sendingReply || ticket.spam_marked_at"
        />
        <div class="mt-4 flex flex-col justify-between gap-4 md:flex-row">
          <div class="flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
            <select
              v-model="sendAnonymously"
              class="min-w-[14rem] rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              :disabled="sendingReply || ticket.spam_marked_at"
            >
              <option value="0">{{ auth.name }}</option>
              <option value="1">Medic 1 team</option>
            </select>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label
                class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                :class="{ 'cursor-not-allowed opacity-60': sendingReply }"
              >
                <input
                  v-model="closeAfterReply"
                  type="checkbox"
                  class="h-3.5 w-3.5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                  :disabled="sendingReply || ticket.spam_marked_at"
                />
                <span>Close after send</span>
              </label>
              <button
                class="accent btn inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
                @click.prevent="reply"
                :disabled="sendingReply || ticket.spam_marked_at || !replyRecipient?.email"
              >
                <span
                  v-if="sendingReply"
                  class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                ></span>
                {{ sendingReply ? "Sending..." : "Send" }}
              </button>
            </div>
          </div>
          <div class="flex flex-1 flex-col items-start gap-3 md:items-end">
            <div v-if="selectedAttachmentNames.length" class="flex flex-wrap gap-2">
              <span
                v-for="attachmentName in selectedAttachmentNames"
                :key="attachmentName"
                class="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200"
              >
                {{ attachmentName }}
              </span>
            </div>
            <button
              class="accent btn disabled:cursor-not-allowed disabled:opacity-60"
              @click.prevent="$refs.fileInput.click()"
              :disabled="sendingReply || ticket.spam_marked_at"
            >
              <PaperClipIcon class="icon"></PaperClipIcon>
              Attach files
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="payrollTransferOpen"
        class="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:px-4 sm:py-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payroll-transfer-title"
        @click.self="closePayrollTransferModal"
        @keydown.esc="closePayrollTransferModal"
      >
        <div class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-slate-800 sm:rounded-2xl">
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <div class="flex items-start gap-3">
              <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
                <ClockIcon class="h-5 w-5" />
              </span>
              <div>
                <h3 id="payroll-transfer-title" class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Send timesheets to payroll
                </h3>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Choose where the ticket timesheets should go.
                </p>
              </div>
            </div>
            <button
              type="button"
              class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:hover:bg-slate-700 dark:hover:text-slate-100"
              :disabled="payrollTransferSubmitting"
              aria-label="Close payroll transfer"
              @click="closePayrollTransferModal"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-5 py-5">
            <div v-if="payrollTransferLoading" class="flex items-center justify-center gap-3 py-16 text-sm text-slate-500 dark:text-slate-400">
              <span class="inline-block h-5 w-5 animate-spin rounded-full border-2 border-violet-300 border-t-violet-700 dark:border-violet-700 dark:border-t-violet-200"></span>
              Preparing payroll period…
            </div>

            <template v-else-if="payrollTransferPreview">
              <div class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">New-run pay period</p>
                <p class="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {{ payrollTransferPreview.period.label }}
                </p>
                <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  This date range is used only if you choose to create a new run.
                </p>
              </div>

              <fieldset class="mt-4">
                <legend class="text-sm font-semibold text-slate-900 dark:text-slate-100">Destination</legend>
                <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Select one of the two actions below.</p>

                <div class="mt-2 grid gap-3 sm:grid-cols-2">
                  <div
                    :class="[
                      'rounded-xl border p-4 transition-colors',
                      payrollDestinationMode === 'existing'
                        ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-300 dark:border-violet-600 dark:bg-violet-950/30 dark:ring-violet-800'
                        : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800',
                      !payrollTransferPreview.available_payroll_runs.length ? 'opacity-60' : '',
                    ]"
                  >
                    <label class="flex cursor-pointer items-start gap-3">
                      <input
                        type="radio"
                        name="payroll-destination"
                        value="existing"
                        class="mt-0.5 h-4 w-4 border-slate-300 text-violet-600 focus:ring-violet-500"
                        :checked="payrollDestinationMode === 'existing'"
                        :disabled="!payrollTransferPreview.available_payroll_runs.length || payrollTransferSubmitting"
                        @change="selectPayrollDestination('existing')"
                      />
                      <span>
                        <span class="block text-sm font-semibold text-slate-900 dark:text-slate-100">Add to an open pay run</span>
                        <span class="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">Adds the files without creating or closing a run.</span>
                      </span>
                    </label>
                    <select
                      v-if="payrollTransferPreview.available_payroll_runs.length"
                      class="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                      :value="selectedPayrollRunId || ''"
                      :disabled="payrollTransferSubmitting"
                      aria-label="Open pay run"
                      @change="selectPayrollDestination('existing', $event.target.value)"
                    >
                      <option
                        v-for="run in payrollTransferPreview.available_payroll_runs"
                        :key="run.id"
                        :value="run.id"
                      >
                        {{ run.name }} ({{ run.pay_period_start }} – {{ run.pay_period_end }})
                      </option>
                    </select>
                    <p v-else class="mt-3 text-xs text-slate-500 dark:text-slate-400">There are no open pay runs.</p>
                  </div>

                  <label
                    :class="[
                      'flex items-start gap-3 rounded-xl border p-4 transition-colors',
                      payrollDestinationMode === 'new'
                        ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-300 dark:border-violet-600 dark:bg-violet-950/30 dark:ring-violet-800'
                        : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800',
                      payrollTransferPreview.can_create_run
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed opacity-60',
                    ]"
                  >
                    <input
                      type="radio"
                      name="payroll-destination"
                      value="new"
                      class="mt-0.5 h-4 w-4 border-slate-300 text-violet-600 focus:ring-violet-500"
                      :checked="payrollDestinationMode === 'new'"
                      :disabled="!payrollTransferPreview.can_create_run || payrollTransferSubmitting"
                      @change="selectPayrollDestination('new')"
                    />
                    <span>
                      <span class="block text-sm font-semibold text-slate-900 dark:text-slate-100">Create a new pay run</span>
                      <span class="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {{ payrollTransferPreview.can_create_run
                          ? `Creates ${payrollTransferPreview.period.label} and closes older open runs.`
                          : payrollTransferPreview.blocked_reason }}
                      </span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <div
                :class="[
                  'mt-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm',
                  payrollTransferPreview.best_ai.enabled
                    ? 'border-cyan-200 bg-cyan-50 text-cyan-900 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100'
                    : 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200',
                ]"
              >
                <SparklesIcon class="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p class="font-semibold">Best AI scan</p>
                  <p class="mt-0.5 text-xs leading-5">
                    {{ payrollTransferPreview.best_ai.enabled
                      ? "Every selected attachment will be queued for Best AI extraction and verification."
                      : "Best AI is not configured, so this transfer cannot be started." }}
                  </p>
                </div>
              </div>

              <div class="mt-5">
                <div class="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100">Attachments</h4>
                    <p class="text-xs text-slate-500 dark:text-slate-400">{{ selectedPayrollAttachmentCount }} selected</p>
                  </div>
                  <span class="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {{ selectablePayrollAttachments.length }} available
                  </span>
                </div>

                <div v-if="payrollTransferPreview.attachments.length" class="space-y-2">
                  <label
                    v-for="attachment in payrollTransferPreview.attachments"
                    :key="attachment.id"
                    :class="[
                      'flex items-center gap-3 rounded-xl border px-3 py-3',
                      payrollAttachmentCanBeSelected(attachment)
                        ? 'cursor-pointer border-slate-200 hover:border-violet-300 dark:border-slate-700 dark:hover:border-violet-700'
                        : 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-70 dark:border-slate-700 dark:bg-slate-900/40',
                    ]"
                  >
                    <input
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                      :checked="selectedPayrollAttachmentIds.has(attachment.id)"
                      :disabled="!payrollAttachmentCanBeSelected(attachment) || payrollTransferSubmitting"
                      @change="togglePayrollAttachment(attachment)"
                    />
                    <DocumentTextIcon class="h-5 w-5 shrink-0 text-slate-400" />
                    <span class="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                      {{ attachment.name }}
                    </span>
                    <span
                      v-if="attachment.transferred && payrollAttachmentCanBeSelected(attachment)"
                      class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/50 dark:text-amber-200"
                    >
                      Move from {{ attachment.payroll_run?.name || "open run" }}
                    </span>
                    <span
                      v-else-if="attachment.transferred"
                      class="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200"
                    >
                      {{ attachment.payroll_run_id === selectedPayrollRunId
                        ? "Already in this run"
                        : attachment.movable ? "Choose another run" : "Already sent" }}
                    </span>
                    <span
                      v-else-if="!attachment.supported"
                      class="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                    >
                      Unsupported
                    </span>
                  </label>
                </div>
                <p v-else class="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
                  This ticket has no attachments.
                </p>
              </div>
            </template>

            <div
              v-if="payrollTransferError"
              class="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
              role="alert"
            >
              {{ payrollTransferError }}
            </div>
          </div>

          <div class="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-800/80 sm:flex-row sm:justify-end">
            <button type="button" class="btn" :disabled="payrollTransferSubmitting" @click="closePayrollTransferModal">
              Cancel
            </button>
            <button
              type="button"
              class="accent btn inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="payrollTransferLoading || payrollTransferSubmitting || !payrollDestinationReady || !payrollTransferPreview?.best_ai?.enabled || selectedPayrollAttachmentCount === 0"
              @click="transferTicketToPayroll"
            >
              <span
                v-if="payrollTransferSubmitting"
                class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
              ></span>
              <SparklesIcon v-else class="h-4 w-4" />
              {{ payrollTransferSubmitLabel }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="infoOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ticket-info-title"
        @click.self="closeInfoModal"
        @keydown.esc="closeInfoModal"
      >
        <div class="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <div class="flex items-start gap-3">
              <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-200">
                <InformationCircleIcon class="h-5 w-5" />
              </span>
              <div>
                <h3 id="ticket-info-title" class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Ticket info
                </h3>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Ticket #{{ ticket.id }} activity and incoming email evidence.
                </p>
              </div>
            </div>
            <button
              type="button"
              class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-100"
              aria-label="Close ticket info"
              @click="closeInfoModal"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <div
            class="flex gap-1 border-b border-slate-200 px-5 pt-2 dark:border-slate-700"
            role="tablist"
            aria-label="Ticket information"
          >
            <button
              type="button"
              role="tab"
              :aria-selected="infoTab === 'activity'"
              :class="[
                'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold transition',
                infoTab === 'activity'
                  ? 'border-cyan-600 text-cyan-700 dark:border-cyan-400 dark:text-cyan-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100',
              ]"
              @click="selectInfoTab('activity')"
            >
              <ClockIcon class="h-4 w-4" />
              Activity
              <span class="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {{ historyEntries.length }}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="infoTab === 'raw'"
              :disabled="!rawEmailItems.length"
              :class="[
                'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40',
                infoTab === 'raw'
                  ? 'border-cyan-600 text-cyan-700 dark:border-cyan-400 dark:text-cyan-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100',
              ]"
              @click="selectInfoTab('raw')"
            >
              <EnvelopeOpenIcon class="h-4 w-4" />
              Raw email
              <span v-if="rawEmailItems.length" class="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {{ rawEmailItems.length }}
              </span>
            </button>
          </div>

          <div v-if="infoTab === 'activity'" class="flex-1 overflow-y-auto overflow-x-hidden px-5 py-5">
            <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100">Ticket activity</h4>
                <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Messages, delivery events, staff actions, and automated decisions.
                </p>
              </div>
              <button class="btn text-xs" :disabled="historyLoading" @click.prevent="loadTicketHistory">
                {{ historyLoading ? "Refreshing…" : "Refresh" }}
              </button>
            </div>

            <div v-if="historyLoading" class="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              Loading activity…
            </div>
            <div v-else-if="!historyEntries.length" class="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              No ticket activity yet.
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="entry in historyEntries"
                :key="entry.id"
                class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40"
              >
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <div class="font-medium text-slate-900 dark:text-slate-100">{{ entry.summary }}</div>
                  <span class="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    {{ formatHistoryEvent(entry.event) }}
                  </span>
                </div>
                <div class="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <span>{{ entry.user?.name || "Neptune System" }}</span>
                  <TimeAgo :data="entry.occurred_at || entry.created_at" />
                </div>
                <div
                  v-if="entry.metadata?.recipient"
                  class="mt-1 text-xs text-slate-600 dark:text-slate-300"
                >
                  Recipient: {{ entry.metadata.recipient }}
                </div>
                <div
                  v-if="historyDetail(entry)"
                  class="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ historyDetail(entry) }}
                </div>
                <p
                  v-if="historyNote(entry)"
                  class="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 dark:text-slate-200"
                >
                  {{ historyNote(entry) }}
                </p>
              </div>
            </div>
          </div>

          <div v-else-if="activeRawEmailItem" class="flex-1 space-y-5 overflow-y-auto px-5 py-5">
            <label v-if="rawEmailItems.length > 1" class="block text-sm font-semibold text-slate-700 dark:text-slate-200">
              Incoming message
              <select
                v-model="rawEmailItemId"
                class="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              >
                <option v-for="item in rawEmailItems" :key="item.id" :value="item.id">
                  #{{ item.id }} — {{ dayjs(item.created_at).format("D MMM YYYY, HH:mm") }} — {{ item.contact?.email || "Unknown sender" }}
                </option>
              </select>
            </label>

            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900/40">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">From</p>
                <p class="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-100">
                  {{ activeRawEmailItem.contact ? formatEmailRecipient(activeRawEmailItem.contact) : "Unknown" }}
                </p>
              </div>
              <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900/40">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">To</p>
                <p class="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-100">
                  {{ activeRawEmailItem.email_to || rawEmailPayload.to || "Unknown" }}
                </p>
              </div>
              <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900/40 sm:col-span-2">
                <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">CC</p>
                <div v-if="ccRecipientsForItem(activeRawEmailItem).length" class="mt-1 flex flex-wrap gap-1.5">
                  <span
                    v-for="recipient in ccRecipientsForItem(activeRawEmailItem)"
                    :key="recipient.email"
                    class="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700 shadow-sm dark:bg-slate-800 dark:text-slate-200"
                  >
                    {{ formatEmailRecipient(recipient) }}
                  </span>
                </div>
                <p v-else class="mt-1 text-sm text-slate-500 dark:text-slate-400">No CC recipients</p>
              </div>
            </div>

            <section
              v-if="(activeRawEmailItem.spam_score !== null && activeRawEmailItem.spam_score !== undefined) || String(activeRawEmailItem.spam_report || '').trim()"
              class="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 dark:border-amber-900 dark:bg-amber-950/20"
            >
              <div class="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-100">
                <ShieldExclamationIcon class="h-5 w-5" />
                {{ spamProviderScoreLabel(activeRawEmailItem) }}
              </div>
              <pre
                v-if="activeRawEmailItem.spam_report"
                class="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-950 p-4 text-xs leading-5 text-slate-100"
              >{{ activeRawEmailItem.spam_report }}</pre>
            </section>

            <section>
              <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100">Original provider payload</h4>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    Stored exactly as received where available. Older messages show a derived fallback.
                  </p>
                </div>
              </div>
              <pre class="max-h-[32rem] overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-950 p-4 text-xs leading-5 text-slate-100">{{ formattedRawEmailPayload }}</pre>
            </section>
          </div>

          <div class="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-800/80">
            <button type="button" class="btn" @click="closeInfoModal">Close</button>
          </div>
        </div>
      </div>

      <div
        v-if="steerOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="steer-reply-title"
        @click.self="closeSteerModal"
        @keydown.esc="closeSteerModal"
      >
        <div class="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <div class="flex items-start gap-3">
              <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
                <SparklesIcon class="h-5 w-5" />
              </span>
              <div>
                <h3 id="steer-reply-title" class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Steer reply with AI
                </h3>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Describe how you want the current draft to change.
                </p>
              </div>
            </div>
            <button
              type="button"
              class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:hover:bg-slate-700 dark:hover:text-slate-100"
              :disabled="steeringReply"
              aria-label="Close AI steer"
              @click="closeSteerModal"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <div class="space-y-4 px-5 py-5">
            <div>
              <label for="steer-prompt" class="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                Instructions
              </label>
              <textarea
                id="steer-prompt"
                ref="steerPromptInput"
                v-model="steerPrompt"
                class="min-h-[140px] w-full rounded-xl border border-slate-300 bg-white p-3 text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                placeholder="For example: Make this warmer and more concise, while keeping the dates and price unchanged."
                maxlength="2000"
                :disabled="steeringReply"
                @keydown.meta.enter.prevent="steerReply"
                @keydown.ctrl.enter.prevent="steerReply"
              ></textarea>
              <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
                The rewrite replaces the text in the reply box. It will not send the message or change attachments.
              </p>
            </div>

            <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-3 dark:border-slate-600 dark:bg-slate-900/40">
              <input
                ref="steerFileInput"
                type="file"
                class="hidden"
                accept=".txt,.md,.csv,.json,.xml,.yaml,.yml,.log,.html,.htm,.xlsx,.xls,.ods,.docx,.pdf,.jpg,.jpeg,.png,.webp"
                multiple
                :disabled="steeringReply"
                @change="onSteerFileChange"
              />
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p class="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    Reference files
                  </p>
                  <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Sent to AI for this rewrite only, not attached to the customer reply.
                  </p>
                </div>
                <button
                  type="button"
                  class="btn inline-flex items-center gap-2"
                  :disabled="steeringReply || steerAttachments.length >= 6"
                  @click="openSteerFilePicker"
                >
                  <PaperClipIcon class="h-4 w-4" />
                  Add files
                </button>
              </div>

              <div v-if="steerAttachments.length" class="mt-3 space-y-2">
                <div
                  v-for="(attachment, index) in steerAttachments"
                  :key="`${attachment.name}-${attachment.size}-${attachment.lastModified}`"
                  class="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                      {{ attachment.name }}
                    </p>
                    <p class="text-xs text-slate-500 dark:text-slate-400">
                      {{ formatSteerAttachmentSize(attachment.size) }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600 disabled:opacity-50 dark:hover:bg-slate-700 dark:hover:text-rose-300"
                    :disabled="steeringReply"
                    :aria-label="`Remove ${attachment.name}`"
                    @click="removeSteerAttachment(index)"
                  >
                    <XMarkIcon class="h-4 w-4" />
                  </button>
                </div>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  Up to six files, 4 MB each. Supports documents, spreadsheets, text files, PDFs, and images.
                </p>
              </div>
            </div>

            <div
              v-if="steerError"
              class="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
              role="alert"
            >
              {{ steerError }}
            </div>
          </div>

          <div class="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-800/80 sm:flex-row sm:justify-end">
            <button
              type="button"
              class="btn"
              :disabled="steeringReply"
              @click="closeSteerModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="accent btn inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="steeringReply || !String(steerPrompt || '').trim()"
              @click="steerReply"
            >
              <span
                v-if="steeringReply"
                class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
              ></span>
              <SparklesIcon v-else class="h-4 w-4" />
              {{ steeringReply ? "Rewriting..." : "Rewrite draft" }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="purchaseInvoiceOpen && activePurchaseInvoice"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6"
        @click.self="closePurchaseInvoiceModal"
      >
        <div class="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-800">
          <div class="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
            <div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Purchase Invoice</h3>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Filed from ticket #{{ ticket.id }}
              </p>
            </div>
            <button class="btn text-xs" @click.prevent="closePurchaseInvoiceModal" aria-label="Close purchase invoice">
              <XMarkIcon class="icon" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-4 py-4">
            <div
              :class="[
                'mb-4 flex items-start gap-3 rounded-lg border px-3 py-3 text-sm',
                ['failed', 'blocked'].includes(activePurchaseInvoice.extraction_status)
                  ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200'
                  : 'border-cyan-200 bg-cyan-50 text-cyan-900 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100',
              ]"
            >
              <span
                v-if="extractionPending"
                class="mt-0.5 inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-cyan-400/40 border-t-cyan-700 dark:border-cyan-300/30 dark:border-t-cyan-100"
              ></span>
              <DocumentTextIcon v-else class="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div class="font-medium">{{ extractionStatusText }}</div>
                <div v-if="extractionFallbackVisible && extractionPending" class="mt-1 text-xs">
                  Extraction is taking longer than expected. The attachment is already filed, so you can enter the details manually.
                </div>
              </div>
            </div>

            <div class="grid gap-4 md:grid-cols-3">
              <div class="space-y-2 md:col-span-3">
                <label class="text-xs uppercase text-slate-500">Supplier</label>
                <div
                  v-if="purchaseInvoiceSupplier"
                  class="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
                >
                  <span>{{ purchaseInvoiceSupplier.company }}</span>
                  <button class="text-xs text-rose-600" @click.prevent="clearPurchaseInvoiceSupplier">
                    Remove
                  </button>
                </div>
                <SearchableInput
                  v-else
                  :key="`purchase-invoice-supplier-${purchaseInvoiceSupplierInputKey}`"
                  url="/api/supplier/search?query="
                  :output="supplierOutput"
                  placeholder="Search suppliers"
                  @selected="selectPurchaseInvoiceSupplier"
                />
              </div>

              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Invoice date
                <input v-model="purchaseInvoiceForm.invoice_date" type="date" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Invoice number
                <input v-model="purchaseInvoiceForm.invoice_number" type="text" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Due date
                <input v-model="purchaseInvoiceForm.due_date" type="date" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                VAT treatment
                <select v-model="purchaseInvoiceForm.vat_treatment" class="input mt-1 w-full">
                  <option
                    v-for="option in vatTreatmentOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Gross
                <input v-model="purchaseInvoiceForm.gross_amount" type="number" step="0.01" min="0" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Net
                <input v-model="purchaseInvoiceForm.net_amount" type="number" step="0.01" min="0" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                VAT
                <input v-model="purchaseInvoiceForm.vat_amount" type="number" step="0.01" min="0" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Category
                <input v-model="purchaseInvoiceForm.category" type="text" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Analysis
                <input v-model="purchaseInvoiceForm.analysis" type="text" class="input mt-1 w-full" />
              </label>
              <div class="text-sm text-slate-500 dark:text-slate-400">
                <div class="text-xs uppercase text-slate-500">Current gross</div>
                <div class="mt-2 font-semibold text-slate-800 dark:text-slate-100">
                  {{ formatCurrency(activePurchaseInvoice.gross_amount) }}
                </div>
              </div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 md:col-span-3">
                Description
                <input v-model="purchaseInvoiceForm.description" type="text" class="input mt-1 w-full" />
              </label>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 md:col-span-3">
                Notes
                <textarea v-model="purchaseInvoiceForm.notes" class="input mt-1 min-h-[90px] w-full"></textarea>
              </label>
            </div>
          </div>

          <div class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
            <RouterLink to="/vat-returns" class="text-sm font-medium text-cyan-700 hover:text-cyan-900 dark:text-cyan-300 dark:hover:text-cyan-100">
              Open VAT workspace
            </RouterLink>
            <div class="flex gap-2">
              <button class="btn text-xs" :disabled="savingPurchaseInvoice" @click.prevent="closePurchaseInvoiceModal">
                Close
              </button>
              <button class="btn accent inline-flex items-center gap-2" :disabled="savingPurchaseInvoice" @click.prevent="savePurchaseInvoiceDetails">
                <span
                  v-if="savingPurchaseInvoice"
                  class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                ></span>
                {{ savingPurchaseInvoice ? "Saving" : "Save details" }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <section class="space-y-3">
        <div class="flex items-end justify-between gap-3 px-1">
          <div>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Conversation</h2>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              Most recent messages appear first.
            </p>
          </div>
          <p class="text-xs font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            {{ conversationItems.length }} messages
          </p>
        </div>

        <div
          class="box text-slate-800 dark:!text-slate-100"
          v-for="item in conversationItems"
          :key="item.id"
        >
          <div class="flex flex-wrap justify-between gap-3">
            <div class="min-w-0 flex-1">
              <div v-if="item.contact" class="break-words text-slate-800 dark:text-slate-100">
                <b>
                  <RouterLink
                    :to="'/contact/' + item.contact.id"
                    class="text-cyan-700 hover:text-cyan-900 dark:text-cyan-300 dark:hover:text-cyan-200"
                  >
                    {{ item.contact?.name }} &lt;{{
                      item.contact?.email ?? "Unknown Contact"
                    }}&gt;
                  </RouterLink>
                </b>
                &nbsp; to &nbsp; <b>Medic 1 Team</b>
              </div>
              <div v-else class="break-words text-slate-800 dark:text-slate-100">
                <b
                  >{{ item.user?.name }} &lt;{{
                    item.user?.email ?? "Unknown User"
                  }}&gt;</b
                >
                <template v-if="item.sendAnonymously === 1">
                  &nbsp;
                  <span>via Medic 1 Team &lt;sales@medic1.uk&gt;</span>
                </template>
                <template v-if="item.recipient">
                  &nbsp; to &nbsp;
                  <b>
                    <RouterLink
                      :to="'/contact/' + item.recipient.id"
                      class="text-cyan-700 hover:text-cyan-900 dark:text-cyan-300 dark:hover:text-cyan-200"
                    >
                      {{ item.recipient.name }} &lt;{{
                        item.recipient.email
                      }}&gt;</RouterLink
                    >
                  </b>
                  <template v-if="getDeliveryStatus(item)">
                    &nbsp;
                    <span
                      :class="[
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                        getDeliveryStatus(item).classes,
                      ]"
                    >
                      {{ getDeliveryStatus(item).label }}
                    </span>
                  </template>
                </template>
              </div>
              <div
                v-if="ccRecipientsForItem(item).length"
                class="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-500 dark:text-slate-400"
              >
                <span class="font-semibold uppercase tracking-[0.12em]">CC</span>
                <span
                  v-for="recipient in ccRecipientsForItem(item)"
                  :key="recipient.email"
                  class="rounded-full bg-slate-100 px-2 py-0.5 dark:bg-slate-700"
                >
                  {{ formatEmailRecipient(recipient) }}
                </span>
              </div>
            </div>
            <div class="shrink-0 text-sm text-slate-500 dark:text-slate-400">
              <TimeAgo :data="item.created_at" />
            </div>
          </div>
          <hr class="my-3 border-slate-200 dark:border-slate-700" />
          <div
            v-if="item.html"
            v-html="item.html"
            class="ticket-html text-slate-800 dark:!text-slate-100 dark:[&_*]:!bg-transparent dark:[&_*]:!text-slate-100 dark:[&_a]:!text-cyan-300 dark:[&_blockquote]:!border-slate-600 dark:[&_code]:!bg-slate-900 dark:[&_pre]:!bg-slate-900 dark:[&_td]:!border-slate-700 dark:[&_th]:!border-slate-700"
          ></div>
          <span v-else class="whitespace-pre-wrap break-all text-slate-800 dark:text-slate-200">{{
            item.text
          }}</span>

          <div
            v-if="quoteSendMetadata(item)"
            class="mt-3 flex flex-wrap items-center gap-3 border-l-4 border-l-cyan-500 bg-cyan-50 px-3 py-2 text-sm dark:bg-cyan-950/30"
          >
            <DocumentTextIcon class="h-5 w-5 shrink-0 text-cyan-700 dark:text-cyan-300" />
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-semibold text-slate-900 dark:text-slate-100">Quote sent</span>
                <span
                  :class="[
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                    quoteStatusClasses(quoteFromMetadata(quoteSendMetadata(item))?.status),
                  ]"
                >
                  {{ quoteStatusLabel(quoteFromMetadata(quoteSendMetadata(item))?.status) }}
                </span>
              </div>
              <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                <RouterLink
                  v-if="quoteFromMetadata(quoteSendMetadata(item))?.id"
                  :to="`/quotes/${quoteFromMetadata(quoteSendMetadata(item)).id}`"
                  class="font-semibold text-cyan-800 hover:text-cyan-950 dark:text-cyan-200 dark:hover:text-cyan-100"
                >
                  {{ quoteFromMetadata(quoteSendMetadata(item))?.display_name }}
                </RouterLink>
                <span v-if="quoteFromMetadata(quoteSendMetadata(item))?.reference_text">
                  {{ quoteFromMetadata(quoteSendMetadata(item))?.reference_text }}
                </span>
              </div>
            </div>
          </div>

          <hr v-if="item.attachments?.length" class="my-3 border-slate-200 dark:border-slate-700" />
          <div
            v-for="attachment in item.attachments"
            :key="attachment.id"
            class="mb-2 flex flex-wrap items-center gap-2"
          >
            <template v-if="attachmentSupportsInlineViewing(attachment)">
              <button
                type="button"
                class="label-bubble inline-flex cursor-pointer items-center gap-2 hover:brightness-110"
                @click="openAttachment(attachment)"
              >
                <EyeIcon class="icon"></EyeIcon>
                {{ attachment.file.filename }}
              </button>
              <a
                :href="attachmentUrl(attachment, { download: true })"
                class="label-bubble inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              >
                <ArrowDownOnSquareIcon class="icon"></ArrowDownOnSquareIcon>
                Download
              </a>
            </template>
            <a
              v-else
              :href="attachmentUrl(attachment, { download: true })"
              class="label-bubble inline-flex items-center gap-2 hover:brightness-110"
            >
              <ArrowDownOnSquareIcon class="icon"></ArrowDownOnSquareIcon>
              {{ attachment.file.filename }}
            </a>
            <RouterLink
              v-if="canFilePurchaseInvoices && attachment.purchase_invoice"
              :to="{
                path: '/supplier-invoices',
                query: { invoice_id: attachment.purchase_invoice.id },
              }"
              class="label-bubble inline-flex items-center gap-2 text-emerald-700 hover:brightness-110 dark:text-emerald-300"
              :aria-label="`Open supplier invoice #${attachment.purchase_invoice.id}`"
              title="Open in Supplier Invoices"
            >
              <DocumentTextIcon class="icon"></DocumentTextIcon>
              Supplier invoice #{{ attachment.purchase_invoice.id }}
            </RouterLink>
            <button
              v-else-if="canFilePurchaseInvoices"
              type="button"
              class="label-bubble inline-flex items-center gap-2 text-cyan-700 hover:brightness-110 disabled:cursor-wait disabled:opacity-60 dark:text-cyan-300"
              :disabled="filingPurchaseInvoiceAttachmentId === attachment.id"
              :aria-label="`File ${attachment.file.filename} as a purchase invoice`"
              title="File as purchase invoice"
              @click.prevent="fileAttachmentAsPurchaseInvoice(attachment)"
            >
              <span
                v-if="filingPurchaseInvoiceAttachmentId === attachment.id"
                class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-400/40 border-t-slate-700 dark:border-slate-500/40 dark:border-t-white"
              ></span>
              <DocumentTextIcon v-else class="icon"></DocumentTextIcon>
              {{
                filingPurchaseInvoiceAttachmentId === attachment.id
                  ? "Filing invoice"
                  : "File invoice"
              }}
            </button>
          </div>
          <div v-if="item.attachments && item.attachments.length > 1">
            <a
              class="label-bubble"
              :href="apiUrl + '/ticket/' + ticket.id + '/attachments/' + item.id"
            >
              <FolderArrowDownIcon class="icon"></FolderArrowDownIcon> Download
              All
            </a>
          </div>
        </div>
      </section>
    </template>

    <template v-else>
      <div class="box animate-pulse space-y-4">
        <div class="h-5 w-32 rounded bg-slate-200 dark:bg-slate-700"></div>
        <div class="h-8 w-2/3 rounded bg-slate-200 dark:bg-slate-700"></div>
        <div class="h-4 w-full rounded bg-slate-200 dark:bg-slate-700"></div>
        <div class="grid gap-3 md:grid-cols-2">
          <div class="h-20 rounded-2xl bg-slate-200 dark:bg-slate-700"></div>
          <div class="h-20 rounded-2xl bg-slate-200 dark:bg-slate-700"></div>
        </div>
      </div>
      <div class="box animate-pulse">
        <div class="h-40 rounded-2xl bg-slate-200 dark:bg-slate-700"></div>
      </div>
      <div class="box animate-pulse">
        <div class="h-28 rounded-2xl bg-slate-200 dark:bg-slate-700"></div>
      </div>
    </template>

    <div
      v-if="acceptanceModal"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4"
      @click.self="acceptanceModal = null"
    >
      <section
        class="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl dark:bg-slate-800"
        role="dialog"
        aria-modal="true"
        aria-labelledby="acceptance-modal-title"
      >
        <h2 id="acceptance-modal-title" class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Accept {{ acceptanceModal.quote.display_name }}?
        </h2>
        <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Choose whether to send the automated acceptance email. It will go to the quote's recipient contact.
        </p>
        <p v-if="acceptanceModal.quote.recipient_contact?.email" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ acceptanceModal.quote.recipient_contact.name || "Recipient" }} · {{ acceptanceModal.quote.recipient_contact.email }}
        </p>

        <label class="mt-4 flex items-start gap-2 text-sm font-medium text-slate-800 dark:text-slate-100">
          <input
            v-model="acceptanceModal.sendEmail"
            type="checkbox"
            class="mt-1 rounded border-slate-300"
            :disabled="!acceptanceModal.quote.sent_via_ticket"
          />
          <span>Send automated acceptance email</span>
        </label>
        <p
          v-if="!acceptanceModal.quote.sent_via_ticket"
          class="ml-6 mt-1 text-xs text-amber-700 dark:text-amber-300"
        >
          This quote has no recorded ticket send, so automated acceptance and event follow-up emails are disabled.
        </p>

        <div v-if="acceptanceModal.sendEmail && acceptanceModal.quote.sent_via_ticket" class="mt-4">
          <label for="acceptance-personal-message" class="block text-sm font-medium text-slate-800 dark:text-slate-100">
            Personal message (optional)
          </label>
          <textarea
            id="acceptance-personal-message"
            v-model="acceptanceModal.personalMessage"
            rows="4"
            maxlength="2000"
            class="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            placeholder="Add a tailored note to the standard booking confirmation."
          ></textarea>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Your note is added to the standard booking details and confirmation.
          </p>
        </div>

        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="btn"
            :disabled="isQuoteActionLoading(acceptanceModal.quote)"
            @click="acceptanceModal = null"
          >
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex min-h-[2.25rem] items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isQuoteActionLoading(acceptanceModal.quote)"
            @click="confirmAcceptAttachedQuote"
          >
            {{ isQuoteActionLoading(acceptanceModal.quote) ? "Accepting..." : "Accept quote" }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.ticket-html {
  line-height: 1.6;
  word-break: break-word;
}

.ticket-html :deep(p) {
  margin: 0 0 0.75rem;
}

.ticket-html :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.ticket-html :deep(blockquote) {
  border-left: 3px solid #d1d5db;
  color: #4b5563;
  margin: 0.75rem 0;
  padding-left: 0.75rem;
}

.ticket-html :deep(ul),
.ticket-html :deep(ol) {
  margin: 0.5rem 0 0.75rem 1.25rem;
}

.ticket-html :deep(pre) {
  background: #f3f4f6;
  border-radius: 6px;
  margin: 0.75rem 0;
  overflow: auto;
  padding: 0.75rem;
}

.ticket-html :deep(code) {
  background: #f3f4f6;
  border-radius: 4px;
  padding: 0.1rem 0.3rem;
}

.ticket-html :deep(table) {
  border-collapse: collapse;
  margin: 0.75rem 0;
  width: 100%;
}

.ticket-html :deep(th),
.ticket-html :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 0.4rem 0.5rem;
  text-align: left;
  vertical-align: top;
}

.history-toggle-btn,
.history-close-btn {
  min-width: 2.25rem;
  padding-left: 0.55rem;
  padding-right: 0.55rem;
}

</style>
